import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { openPrintWindow } from "@/lib/printWindow";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

function IseeInput({ alunno, onCommit }) {
  const [val, setVal] = useState(alunno.isee != null ? String(alunno.isee) : "");
  useEffect(() => { setVal(alunno.isee != null ? String(alunno.isee) : ""); }, [alunno.CodFis, alunno.isee]);
  const commit = () => {
    const trimmed = val.trim().replace(/\./g, "").replace(",", ".");
    if (trimmed === "" && alunno.isee == null) return;
    if (trimmed === "") return onCommit("");
    const n = Number(trimmed);
    if (Number.isNaN(n)) return;
    if (n === Number(alunno.isee || 0)) return;
    onCommit(n);
  };
  return (
    <input type="text" inputMode="decimal"
      value={val}
      onChange={e => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
      disabled={!alunno.CodFis}
      data-testid={`mensa-mese-isee-${alunno.CodFis}`}
      placeholder="—"
      style={{
        width: "100%", padding: "4px 6px",
        border: "1px solid #b2dfdb", background: alunno.isee != null ? "#e0f7fa" : "white",
        color: "#006064", fontSize: 12, fontWeight: 700, fontFamily: "monospace",
        textAlign: "right", boxSizing: "border-box",
      }}
    />
  );
}

export default function MensaMesePage() {
  const navigate = useNavigate();
  const { gestione } = useParams();
  const [searchParams] = useSearchParams();
  const today = new Date();
  // Anno/mese fissi dai query params: ?anno=2026&mese=4
  // Se mancano, redirect alla lista periodi.
  const paramAnno = Number(searchParams.get("anno"));
  const paramMese = Number(searchParams.get("mese"));
  useEffect(() => {
    if (!paramAnno || !paramMese) {
      navigate("/g/mensa_mese/mensa_mese_periodi", { replace: true });
    }
  }, [paramAnno, paramMese, navigate]);
  const mese = paramMese || today.getMonth() + 1;
  const anno = paramAnno || today.getFullYear();
  // Anno scolastico derivato da anno+mese
  const annoScol = mese >= 9 ? anno : anno - 1;
  // Anno ISEE: prima da sessionStorage (preferenza utente nella sessione), poi caricato da app_settings
  const [annoIsee, setAnnoIsee] = useState(() => {
    try {
      const s = sessionStorage.getItem("mensa_anno_isee");
      if (s && !Number.isNaN(Number(s))) return Number(s);
    } catch { /* ignore */ }
    return null;
  });
  const [data, setData] = useState({ totale_alunni: 0, totale_presenze: 0, anno_scolastico: null, anno_isee: null, giorni_scuola: 0, data: [] });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [periodi, setPeriodi] = useState([]);
  const [importing, setImporting] = useState(false);
  const [giorniInput, setGiorniInput] = useState(0);
  const [dataDa, setDataDa] = useState("");
  const [dataA, setDataA] = useState("");
  // Tab filtro per flag: tutti | esente | privato | fratelli | agevolato | non_residente
  const [flagTab, setFlagTab] = useState("tutti");
  // Tab filtro per settore: tutti | A | E | F | M | _ALTRO_
  const [settoreTab, setSettoreTab] = useState("tutti");
  // Pannello parametri di gestione
  const [paramsOpen, setParamsOpen] = useState(false);
  // Modale storico modifiche spunte (audit log)
  const [logOpen, setLogOpen] = useState(false);
  const [logRows, setLogRows] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const fileInputRef = useRef(null);
  // Modale storico ISEE per singolo alunno
  const [iseeModal, setIseeModal] = useState(null); // { alunno, list }
  const [giorniModal, setGiorniModal] = useState(null); // { Cog, Nom, presenze, giorni }
  const [selAddebiti, setSelAddebiti] = useState(() => new Set()); // Cod selezionati per addebiti
  const [addebitiModal, setAddebitiModal] = useState(false);
  const [addebitiForm, setAddebitiForm] = useState({ data_scadenza: "", descrizione_a: "", descrizione_efm: "", causale: 2 });
  const [addebitiBusy, setAddebitiBusy] = useState(false);
  const [addebitiResult, setAddebitiResult] = useState(null);
  // Chiavi delle righe il cui "Valore €" è variato dopo l'ultimo aggiornamento (marker "!")
  const [changedVals, setChangedVals] = useState(() => new Set());
  const [aggiornando, setAggiornando] = useState(false);
  const [allineando, setAllineando] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Chiave stabile di riga per il confronto dei valori (CF, fallback Cod)
  const rowKey = (a) => String(a.CodFis || a.Cod || "");

  // Modifica inline dell'importo reale addebitato su Segremat (scrive sul gestionale)
  const [editMovKey, setEditMovKey] = useState(null); // codice_movimento in modifica
  const [editMovVal, setEditMovVal] = useState("");
  const [savingImporto, setSavingImporto] = useState(false);

  // Carica anno ISEE globale dal config SOLO se l'utente non ha già una preferenza in sessione
  useEffect(() => {
    if (annoIsee != null) return; // già impostato (da sessionStorage o user)
    (async () => {
      try {
        const r = await api.get(`/app-settings`);
        const ag = r.data?.anno_isee_gestito;
        if (ag) setAnnoIsee(Number(ag));
      } catch (e) { console.error(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persisti l'anno ISEE selezionato dall'utente per mantenerlo tra navigazioni dentro Mensa Mese
  useEffect(() => {
    if (annoIsee != null) {
      try { sessionStorage.setItem("mensa_anno_isee", String(annoIsee)); } catch { /* ignore */ }
    }
  }, [annoIsee]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { anno, mese };
      if (annoIsee) params.anno_isee = annoIsee;
      const r = await api.get(`/mensa-mese/alunni`, { params });
      setData(r.data);
      setChangedVals(new Set()); // reset marker "!" ad ogni ricaricamento normale (cambio mese/ISEE/import)
      setGiorniInput(r.data.giorni_scuola || 0);
      setDataDa(r.data.data_da || "");
      setDataA(r.data.data_a || "");
      // Allinea selettore con valore risolto dal backend (prima volta)
      if (!annoIsee && r.data.anno_isee) setAnnoIsee(Number(r.data.anno_isee));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [anno, mese, annoIsee]);

  // Ricalcola i valori in base ai flag attuali ed evidenzia con "!" quelli variati
  // rispetto a ciò che è attualmente mostrato a schermo.
  const handleAggiorna = async () => {
    setAggiornando(true);
    try {
      const prev = new Map(data.data.map(a => [rowKey(a), Number(a.valore_mensa_mese || 0)]));
      const params = { anno, mese };
      if (annoIsee) params.anno_isee = annoIsee;
      const r = await api.get(`/mensa-mese/alunni`, { params });
      const fresh = r.data;
      const changed = new Set();
      for (const a of (fresh.data || [])) {
        const key = rowKey(a);
        const before = prev.get(key);
        const now = Number(a.valore_mensa_mese || 0);
        if (before != null && Math.abs(before - now) > 0.001) changed.add(key);
      }
      setData(fresh);
      setGiorniInput(fresh.giorni_scuola || 0);
      setDataDa(fresh.data_da || "");
      setDataA(fresh.data_a || "");
      setChangedVals(changed);
    } catch (e) {
      alert("Errore aggiornamento: " + (e.response?.data?.detail || e.message));
    } finally {
      setAggiornando(false);
    }
  };

  const fetchPeriodi = useCallback(async () => {
    try {
      const r = await api.get(`/mensa-mese/disponibili`);
      setPeriodi(r.data || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); fetchPeriodi(); }, [fetchData, fetchPeriodi]);

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ""; // reset per permettere lo stesso file due volte
    if (!window.confirm(
      `📤 Importare il file "${file.name}"?\n\n` +
      `I dati esistenti per ${MESI[mese - 1]} ${anno} verranno SOSTITUITI (replace).`
    )) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("anno", String(anno));
      fd.append("mese", String(mese));
      fd.append("replace", "true");
      const r = await api.post(`/mensa-mese/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      alert(
        `✅ Importazione completata\n\n` +
        `Periodo: ${MESI[r.data.mese - 1]} ${r.data.anno}\n` +
        `Alunni importati: ${r.data.alunni_importati}\n` +
        `Presenze totali: ${r.data.presenze_totali}\n` +
        `Righe lette: ${r.data.righe_lette}`
      );
      // Se il file era di un periodo diverso, naviga alla URL del periodo importato
      if (r.data.mese !== mese || r.data.anno !== anno) {
        navigate(`/g/mensa_mese/mensa_mese_alunni?anno=${r.data.anno}&mese=${r.data.mese}`, { replace: true });
      }
      await fetchPeriodi();
    } catch (err) {
      alert("Errore importazione: " + (err.response?.data?.detail || err.message));
    } finally { setImporting(false); }
  };

  const saveGiorniScuola = async () => {
    try {
      await api.put(`/mensa-mese/giorni-scuola`, null, {
        params: { anno, mese, giorni: Number(giorniInput), data_da: dataDa || "", data_a: dataA || "" }
      });
      // Ricarica per aggiornare assenze
      await fetchData();
    } catch (e) { alert("Errore salvataggio giorni: " + (e.response?.data?.detail || e.message)); }
  };

  const savePeriodoDate = async () => {
    try {
      await api.put(`/mensa-mese/giorni-scuola`, null, {
        params: { anno, mese, giorni: Number(giorniInput), data_da: dataDa || "", data_a: dataA || "" }
      });
      await fetchData();
    } catch (e) { alert("Errore salvataggio periodo: " + (e.response?.data?.detail || e.message)); }
  };

  const apriLogSpunte = async () => {
    setLogOpen(true);
    setLogLoading(true);
    try {
      const r = await api.get("/mensa-mese/flags/log", { params: { anno, mese, limit: 1000 } });
      setLogRows(r.data?.data || []);
    } catch (e) {
      console.error("log load failed", e);
      setLogRows([]);
    } finally {
      setLogLoading(false);
    }
  };

  const toggleFlag = async (alunno, flag) => {
    if (!alunno.CodFis) { alert("Alunno senza codice fiscale: impossibile salvare flag."); return; }
    const newVal = !alunno[flag];
    const key = rowKey(alunno);
    const prevValore = Number(alunno.valore_mensa_mese || 0);
    // "Spariscono appena tocco di nuovo un flag": azzera i marker "!" precedenti
    setChangedVals(new Set());
    // Update ottimistico della spunta
    setData(prev => ({
      ...prev,
      data: prev.data.map(a => (a.CodFis === alunno.CodFis ? { ...a, [flag]: newVal } : a)),
    }));
    try {
      await api.put(`/mensa-mese/${anno}/${mese}/${alunno.CodFis}/flag`, null, { params: { flag, value: newVal } });
      // Ricalcolo automatico dei valori (il backend ricalcola a ogni GET)
      const params = { anno, mese };
      if (annoIsee) params.anno_isee = annoIsee;
      const r = await api.get(`/mensa-mese/alunni`, { params });
      const fresh = r.data;
      setData(fresh);
      // Evidenzia con "!" la riga toccata solo se il suo valore è effettivamente cambiato
      const freshRow = (fresh.data || []).find(a => rowKey(a) === key);
      if (freshRow && Math.abs(Number(freshRow.valore_mensa_mese || 0) - prevValore) > 0.001) {
        setChangedVals(new Set([key]));
      } else {
        setChangedVals(new Set());
      }
    } catch (e) {
      console.error("flag save failed", e);
      // rollback
      setData(prev => ({ ...prev, data: prev.data.map(a => (a.CodFis === alunno.CodFis ? { ...a, [flag]: !newVal } : a)) }));
    }
  };

  const handlePrint = () => {
    // Costruisce HTML stampabile: una classe per pagina con totale classe.
    // Mantiene le evidenziazioni (ISEE/agevolato in verde, presenze=0 in rosso).
    const fmtN = (n) => Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    // Raggruppa per plesso/classe
    const byPlesso = {};
    for (const a of filtered) {
      const plesso = (a.settore || "").toUpperCase().split(",")[0].trim() || "-";
      const classe = a.classe || "-";
      if (!byPlesso[plesso]) byPlesso[plesso] = {};
      if (!byPlesso[plesso][classe]) byPlesso[plesso][classe] = [];
      byPlesso[plesso][classe].push(a);
    }
    const PLESSI_LABEL = { A: "Infanzia", E: "Primaria E", F: "Primaria F", M: "Secondaria" };
    let html = `<!doctype html><html><head><meta charset="utf-8"><title>Mensa Mese — ${MESI[mese - 1]} ${anno}</title>
      <style>
        @page { size: A4 portrait; margin: 12mm; }
        body { font-family: Arial, sans-serif; font-size: 11px; color: #222; margin: 0; }
        .class-page { page-break-after: always; }
        .class-page:last-child { page-break-after: auto; }
        h1 { font-size: 18px; margin: 0 0 4px; color: #4a148c; }
        h2 { font-size: 14px; margin: 6px 0; color: #1565c0; border-bottom: 2px solid #1565c0; padding-bottom: 4px; }
        .meta { font-size: 11px; color: #555; margin-bottom: 8px; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #4a148c; color: white; padding: 5px 6px; text-align: left; font-size: 10px; text-transform: uppercase; }
        td { padding: 4px 6px; border-bottom: 1px solid #eee; font-size: 11px; }
        td.num { text-align: right; font-family: monospace; font-weight: 700; }
        tr.highlight-isee { background: #bdbdbd !important; color: #000; font-weight: 700; }
        tr.highlight-agev { background: #2e7d32 !important; color: #c8e6c9; font-weight: 700; }
        tr.highlight-zero { background: #ff8a80 !important; color: #b71c1c; font-weight: 700; }
        tr.highlight-orphan { background: #ffe0b2 !important; color: #e65100; font-weight: 700; }
        tr.tot { background: #e1bee7; font-weight: 800; }
        tr.tot td { padding-top: 6px; padding-bottom: 6px; color: #4a148c; font-size: 12px; }
        .footer-note { font-size: 9px; color: #999; margin-top: 4px; }
        .legend { display: flex; gap: 14px; margin-bottom: 10px; font-size: 10px; }
        .legend span { display: inline-block; padding: 2px 8px; border: 1px solid #aaa; }
      </style>
    </head><body>`;
    Object.entries(byPlesso).sort().forEach(([plesso, classi]) => {
      Object.entries(classi).sort().forEach(([classe, list]) => {
        const totPres = list.reduce((a, b) => a + (Number(b.presenze) || 0), 0);
        const totAss = list.reduce((a, b) => a + (Number(b.assenze) || 0), 0);
        const totVal = list.reduce((a, b) => a + (Number(b.valore_mensa_mese) || 0), 0);
        html += `<section class="class-page">
          <h1>${MESI[mese - 1]} ${anno} · Plesso ${plesso} — ${PLESSI_LABEL[plesso] || plesso} · Classe ${classe}</h1>
          <div class="meta">AS ${annoScol}/${String(annoScol + 1).slice(2)} · Giorni scuola: <b>${data.giorni_scuola || 0}</b> · Anno ISEE: <b>${data.anno_isee || annoIsee || "—"}</b> · Alunni in classe: <b>${list.length}</b></div>
          <div class="legend"><span style="background:#bdbdbd;font-weight:800;color:#000">ISEE valorizzato</span><span style="background:#2e7d32;font-weight:800;color:#c8e6c9">Agevolato</span><span style="background:#ff8a80;font-weight:800;color:#b71c1c">Presenze 0</span><span style="background:#ffe0b2;font-weight:800;color:#e65100">Orfano</span></div>
          <table>
            <thead><tr>
              <th style="width:24px">#</th>
              <th>Cognome</th><th>Nome</th>
              <th style="width:120px">Codice Fiscale</th>
              <th style="width:60px" class="num">ISEE</th>
              <th style="width:50px" class="num">Pres.</th>
              <th style="width:50px" class="num">Ass.</th>
              <th style="width:40px">Es</th><th style="width:40px">Priv</th><th style="width:40px">Fr</th><th style="width:40px">Ag</th><th style="width:40px">NR</th>
              <th style="width:70px" class="num">Valore €</th>
            </tr></thead>
            <tbody>`;
        list.forEach((a, i) => {
          const hasIsee = (a.isee != null && a.isee !== "" && Number(a.isee) > 0);
          const isAgev = !!a.agevolato;
          const zero = (Number(a.presenze) || 0) === 0;
          // Priorità: Agevolato > ISEE > Presenze 0 > Orfano
          const cls = isAgev ? "highlight-agev"
            : hasIsee ? "highlight-isee"
            : zero ? "highlight-zero"
            : a.orphan ? "highlight-orphan"
            : "";
          html += `<tr class="${cls}">
            <td>${i + 1}</td>
            <td>${(a.Cog || "").replace(/</g, "&lt;")}</td>
            <td>${(a.Nom || "").replace(/</g, "&lt;")}</td>
            <td style="font-family:monospace;font-size:10px">${a.CodFis || ""}</td>
            <td class="num">${a.isee != null && a.isee !== "" ? fmtN(a.isee) : ""}</td>
            <td class="num">${a.presenze || 0}</td>
            <td class="num">${data.giorni_scuola ? (a.assenze || 0) : ""}</td>
            <td style="text-align:center">${a.esente ? "✓" : ""}</td>
            <td style="text-align:center">${a.privato ? "✓" : ""}</td>
            <td style="text-align:center">${a.fratelli ? "✓" : ""}</td>
            <td style="text-align:center">${a.agevolato ? "✓" : ""}</td>
            <td style="text-align:center">${a.non_residente ? "✓" : ""}</td>
            <td class="num">${a.valore_mensa_mese != null ? fmtN(a.valore_mensa_mese) : ""}</td>
          </tr>`;
        });
        html += `</tbody><tfoot>
          <tr class="tot">
            <td colSpan="5" style="text-align:right">Totale Classe ${classe} (${list.length} alunni)</td>
            <td class="num">${totPres}</td>
            <td class="num">${data.giorni_scuola ? totAss : ""}</td>
            <td colSpan="5"></td>
            <td class="num" style="font-size:14px">${fmtN(totVal)}</td>
          </tr>
          </tfoot></table>
          <div class="footer-note">canossaHUB · stampa generata ${new Date().toLocaleString("it-IT")}</div>
        </section>`;
      });
    });
    html += `</body></html>`;
    const win = openPrintWindow(html, { autoPrint: true });
    if (!win) { alert("Permetti i pop-up del browser per la stampa."); }
  };

  const setIseeAlunno = async (alunno, importo) => {
    if (!alunno.CodFis) { alert("Alunno senza codice fiscale: impossibile salvare ISEE."); return; }
    if (!annoIsee) { alert("Anno ISEE non ancora caricato."); return; }
    const newVal = importo === "" || importo == null ? null : Number(importo);
    setData(prev => ({
      ...prev,
      data: prev.data.map(a => (a.CodFis === alunno.CodFis ? { ...a, isee: newVal, isee_anno: annoIsee } : a)),
    }));
    try {
      await api.put(`/mensa-mese/isee/${alunno.CodFis}`, null, { params: { anno_scol: annoIsee, importo: newVal == null ? 0 : newVal } });
    } catch (e) {
      console.error("isee save failed", e);
      await fetchData();
    }
  };

  const filtered = data.data.filter(a => {
    const settoriAlunno = (a.settore || "").toUpperCase().split(",").map(x => x.trim()).filter(Boolean);
    const hasDidattico = settoriAlunno.some(x => ["A", "E", "F", "M"].includes(x));
    if (flagTab === "addebito_zero") {
      if (!hasDidattico) return false;
      if (Number(a.valore_mensa_mese || 0) !== 0) return false;
    } else if (flagTab === "divergenti_segremat") {
      if (a.importo_segremat == null) return false;
      if (Math.abs(Number(a.importo_segremat) - Number(a.valore_mensa_mese || 0)) <= 0.001) return false;
    } else if (flagTab !== "tutti" && !a[flagTab]) {
      return false;
    }
    if (settoreTab === "tutti") {
      // "TUTTI" = settori didattici A/E/F/M + alunni orfani (CF non in anagrafica)
      if (!hasDidattico && !a.orphan) return false;
    } else if (settoreTab === "_ALTRO_") {
      // Altro = nessun settore didattico (orfani inclusi qui se non hanno settore didattico)
      if (hasDidattico) return false;
    } else {
      if (!settoriAlunno.includes(settoreTab)) return false;
    }
    if (search) {
      const s = search.toLowerCase();
      return ((a.Cog || "").toLowerCase().includes(s) ||
              (a.Nom || "").toLowerCase().includes(s) ||
              (a.CodFis || "").toLowerCase().includes(s) ||
              (a.classe || "").toLowerCase().includes(s));
    }
    return true;
  });

  // Alunni selezionabili per creazione addebiti: tutti gli alunni DIDATTICI
  // (settore A/E/F/M), INCLUSE le righe con valore € = 0 (es. esenti o 0 presenze).
  // Le righe non-alunno (_ALTRO_/orfani) hanno sempre valore 0 e restano escluse.
  const selectableAddebiti = filtered.filter(a => (a.settore || "").toUpperCase().split(",").some(s => ["A", "E", "F", "M"].includes(s.trim())) && Number(a.valore_mensa_mese || 0) > 0);
  const selCount = selAddebiti.size;
  // Contatore divergenze: righe mostrate con importo reale Segremat ≠ calcolo
  const segMatched = filtered.filter(a => a.importo_segremat != null).length;
  const diffCount = filtered.filter(a => a.importo_segremat != null && Math.abs(Number(a.importo_segremat) - Number(a.valore_mensa_mese || 0)) > 0.001).length;
  // Addebiti Segremat allineabili: esistenti, NON pagati, con importo reale diverso dal calcolo
  const allineabili = filtered.filter(a => a.importo_segremat != null && a.codice_movimento_segremat != null && !a.pagato_segremat && Math.abs(Number(a.importo_segremat) - Number(a.valore_mensa_mese || 0)) > 0.001);
  const handleAllinea = async () => {
    if (allineabili.length === 0) return;
    const ok = window.confirm(
      `Allineo ${allineabili.length} addebiti Segremat al valore CALCOLATO dalla tabella?\n\n` +
      `Gli importi reali su Segremat verranno SOVRASCRITTI con i valori della colonna "Valore €".\n` +
      `(Esclusi gli addebiti già pagati.)\n\n` +
      `ATTENZIONE: questa operazione scrive DIRETTAMENTE sul gestionale Segremat.`
    );
    if (!ok) return;
    setAllineando(true);
    try {
      const items = allineabili.map(a => ({
        anno: a.anno_segremat != null ? a.anno_segremat : paramAnno,
        codice_alunno: a.Cod,
        codice_movimento: a.codice_movimento_segremat,
        importo: Number(a.valore_mensa_mese || 0),
      }));
      const r = await api.post("/riconciliazione/allinea-importi", { items });
      const errs = r.data?.errors || [];
      if (errs.length) {
        alert(`Allineamento completato con ${errs.length} errori su ${items.length}.\n\n` + errs.slice(0, 6).join("\n"));
      } else {
        alert(`✓ ${items.length} addebiti allineati su Segremat.`);
      }
      await fetchData();
    } catch (e) {
      alert("Errore allineamento: " + (e.response?.data?.detail || e.message));
    } finally {
      setAllineando(false);
    }
  };
  const selKey = (a) => a.Cod || a.CodFis;
  const allSelSelected = selectableAddebiti.length > 0 && selectableAddebiti.every(a => selAddebiti.has(selKey(a)));
  const toggleSelOne = (cod) => setSelAddebiti(prev => {
    const n = new Set(prev);
    if (n.has(cod)) n.delete(cod); else n.add(cod);
    return n;
  });
  const toggleSelAll = () => setSelAddebiti(prev => {
    if (selectableAddebiti.every(a => prev.has(selKey(a)))) return new Set();
    return new Set(selectableAddebiti.map(a => selKey(a)));
  });
  // Scarica da Segremat i movimenti aggiornati (necessario per vedere addebiti di nuovi mesi
  // o creati/modificati direttamente su Segremat). Sincronizza l'anno scolastico e l'anno solare.
  const handleSyncSegremat = async () => {
    const annoSc = mese < 9 ? anno - 1 : anno;
    const anni = Array.from(new Set([annoSc, anno]));
    const ok = window.confirm(
      `Sincronizzo i movimenti Segremat degli anni ${anni.join(", ")}?\n\n` +
      `Serve a scaricare in locale gli addebiti presenti su Segremat (es. nuovi mesi come Settembre). ` +
      `Può richiedere qualche secondo.`
    );
    if (!ok) return;
    setSyncing(true);
    try {
      const r = await api.post("/riconciliazione/sync", { anni }, { timeout: 180000 });
      await fetchData();
      const esiti = r.data?.esiti || [];
      alert("✓ Sincronizzazione completata.\n" + esiti.map(e => `Anno ${e.anno}: ${e.ok ? (e.movimenti + " movimenti") : ("ERRORE " + e.errore)}`).join("\n"));
    } catch (e) {
      alert("Errore sincronizzazione Segremat: " + (e.response?.data?.detail || e.message));
    } finally {
      setSyncing(false);
    }
  };

  const startEditImporto = (a) => {    setEditMovKey(a.codice_movimento_segremat);
    setEditMovVal(a.importo_segremat != null ? Number(a.importo_segremat).toFixed(2) : "");
  };
  const cancelEditImporto = () => { setEditMovKey(null); setEditMovVal(""); };
  const saveImporto = async (a) => {
    const nuovo = parseFloat(String(editMovVal).replace(",", "."));
    if (isNaN(nuovo) || nuovo < 0) { alert("Importo non valido"); return; }
    if (Math.abs(nuovo - Number(a.importo_segremat || 0)) < 0.001) { cancelEditImporto(); return; }
    const ok = window.confirm(
      `Confermi la modifica dell'addebito Segremat di ${a.Cog} ${a.Nom}?\n\n` +
      `Da € ${Number(a.importo_segremat || 0).toFixed(2)} a € ${nuovo.toFixed(2)}\n\n` +
      `ATTENZIONE: questa operazione scrive DIRETTAMENTE sul gestionale Segremat.`
    );
    if (!ok) return;
    setSavingImporto(true);
    try {
      await api.post("/riconciliazione/modifica-importo", {
        anno: a.anno_segremat != null ? a.anno_segremat : paramAnno,
        codice_alunno: a.Cod,
        codice_movimento: a.codice_movimento_segremat,
        importo: nuovo,
      });
      cancelEditImporto();
      await fetchData();
    } catch (e) {
      alert("Errore modifica importo Segremat: " + (e.response?.data?.detail || e.message));
    } finally {
      setSavingImporto(false);
    }
  };

  const openAddebitiModal = () => {    if (selCount === 0) return;
    setAddebitiResult(null);
    setAddebitiForm({
      data_scadenza: "",
      descrizione_a: `Retta Mensa Mese di ${MESI[mese - 1]} anno ${anno}`,
      descrizione_efm: `Retta Mensa Mese di ${MESI[mese - 1]} anno ${anno} pasti nr #`,
      causale: 2,
    });
    setAddebitiModal(true);
  };
  const inviaAddebiti = async () => {
    const ds = (addebitiForm.data_scadenza || "").trim();
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(ds)) { alert("Data scadenza non valida (formato gg/mm/aaaa)"); return; }
    const dA = (addebitiForm.descrizione_a || "").trim();
    const dEFM = (addebitiForm.descrizione_efm || "").trim();
    if (!dA || !dEFM) { alert("Le descrizioni (Settore A e Settori E/F/M) sono obbligatorie"); return; }
    const settoreOf = (a) => ((a.settore || "").toUpperCase().split(",").map(x => x.trim()).filter(Boolean)[0] || "");
    const byCod = new Map(selectableAddebiti.map(a => [selKey(a), a]));
    const addebiti = [...selAddebiti].map(cod => byCod.get(cod)).filter(Boolean).map(a => {
      const pasti = Number(a.presenze) || 0;
      const descrizione = settoreOf(a) === "A" ? dA : dEFM.split("#").join(String(pasti));
      return { codice_alunno: a.Cod ?? null, importo: Number(a.valore_mensa_mese) || 0, cognome: a.Cog, nome: a.Nom, descrizione };
    });
    setAddebitiBusy(true);
    setAddebitiResult(null);
    try {
      const r = await api.post("/riconciliazione/crea-addebiti", {
        anno: paramAnno, data_scadenza: ds, descrizione: dA,
        causale: Number(addebitiForm.causale) || 2, addebiti,
      });
      setAddebitiResult(r.data);
      if (r.data?.n_ok > 0) setSelAddebiti(new Set()); // svuota i selezionati creati
    } catch (e) {
      alert("Errore creazione addebiti: " + (e.response?.data?.detail || e.message));
    } finally {
      setAddebitiBusy(false);
    }
  };

  // Conteggi per tab flag (limitati agli alunni dei settori didattici + orfani, coerenti col tab "Tutti")
  const _setOf = (a) => (a.settore || "").toUpperCase().split(",").map(x => x.trim()).filter(Boolean);
  const _isDidattico = (a) => _setOf(a).some(x => ["A", "E", "F", "M"].includes(x));
  const didatticiAll = data.data.filter(a => _isDidattico(a) || a.orphan);
  const flagCounts = {
    tutti: didatticiAll.length,
    esente: didatticiAll.filter(a => a.esente).length,
    privato: didatticiAll.filter(a => a.privato).length,
    fratelli: didatticiAll.filter(a => a.fratelli).length,
    agevolato: didatticiAll.filter(a => a.agevolato).length,
    non_residente: didatticiAll.filter(a => a.non_residente).length,
    addebito_zero: didatticiAll.filter(a => _isDidattico(a) && Number(a.valore_mensa_mese || 0) === 0).length,
    divergenti_segremat: didatticiAll.filter(a => a.importo_segremat != null && Math.abs(Number(a.importo_segremat) - Number(a.valore_mensa_mese || 0)) > 0.001).length,
  };

  // Conteggi per tab settore: "Tutti" = didattici A/E/F/M + orfani
  const settoreCounts = {
    tutti: didatticiAll.length,
    A: data.data.filter(a => _setOf(a).includes("A")).length,
    E: data.data.filter(a => _setOf(a).includes("E")).length,
    F: data.data.filter(a => _setOf(a).includes("F")).length,
    M: data.data.filter(a => _setOf(a).includes("M")).length,
    _ALTRO_: data.data.filter(a => {
      const s = _setOf(a);
      return (s.length > 0 && !s.some(x => ["A", "E", "F", "M"].includes(x))) || (a.orphan && !_isDidattico(a));
    }).length,
  };

  const isImported = data.totale_presenze > 0;

  return (
    <div>
      <div className="sw-page-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ margin: 0 }}>📅 Mensa Mese — Presenze</h1>
          <div className="sw-breadcrumb">Home &gt; Mensa Mese</div>
        </div>
        <button onClick={apriLogSpunte} data-testid="mensa-mese-log-spunte-btn"
          title="Storico modifiche delle spunte (Es. Priv. Fr. Agev. N.R.) di questo mese"
          style={{
            height: 36, padding: "0 14px",
            background: "white",
            color: "#bf360c",
            border: "2px solid #bf360c",
            fontSize: 12, fontWeight: 800, cursor: "pointer",
            display: "flex", alignItems: "center", gap: 6, letterSpacing: 0.5, textTransform: "uppercase",
          }}>📝 Log spunte</button>
      </div>

      {/* Header con selettori e import — compatto, una riga unica */}
      <div style={{ margin: "12px 20px", padding: 8, background: "linear-gradient(180deg, #f3e5f5 0%, #e1bee7 100%)", border: "2px solid #6a1b9a", display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
        <button onClick={() => navigate("/g/mensa_mese/mensa_mese_periodi")} data-testid="mensa-mese-back-periodi"
          title="Torna alla lista dei mesi"
          style={{
            height: 36, padding: "0 12px",
            background: "linear-gradient(180deg, #f57c00 0%, #e65100 100%)",
            color: "white",
            border: "2px solid #bf360c",
            fontSize: 11, fontWeight: 800, cursor: "pointer",
            display: "flex", alignItems: "center", gap: 4, letterSpacing: 0.3, textTransform: "uppercase",
            boxShadow: "0 2px 6px rgba(230,81,0,0.4)",
          }}>← Mesi</button>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: 9, fontWeight: 800, color: "#4a148c", letterSpacing: 0.4 }}>MESE</span>
          <div data-testid="mensa-mese-fixed-mese"
            style={{ height: 36, padding: "0 10px", display: "flex", alignItems: "center", fontSize: 13, fontWeight: 800, border: "2px solid #6a1b9a", background: "#fff8e1", color: "#4a148c", minWidth: 90 }}>
            {MESI[mese - 1]}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: 9, fontWeight: 800, color: "#4a148c", letterSpacing: 0.4 }}>A.S.</span>
          <div data-testid="mensa-mese-fixed-anno-scol"
            style={{ height: 36, padding: "0 10px", display: "flex", alignItems: "center", fontSize: 13, fontWeight: 800, border: "2px solid #6a1b9a", background: "#fff8e1", color: "#4a148c", fontFamily: "monospace", minWidth: 78 }}>
            {annoScol}/{String(annoScol + 1).slice(2)}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: 9, fontWeight: 800, color: "#00838f", letterSpacing: 0.4 }} title="Anno ISEE da agganciare. Default da Impostazioni → Anno ISEE gestito.">ANNO ISEE</span>
          <select value={annoIsee ?? ""} onChange={e => setAnnoIsee(e.target.value === "" ? null : Number(e.target.value))} data-testid="mensa-mese-sel-anno-isee"
            style={{ height: 36, padding: "0 8px", fontSize: 13, fontWeight: 800, border: "2px solid #00838f", background: "#e0f7fa", color: "#006064", fontFamily: "monospace", minWidth: 80, boxSizing: "border-box" }}>
            {(() => {
              const base = annoIsee || data.anno_isee || annoScol;
              const years = new Set();
              for (let y = base - 5; y <= base + 1; y += 1) years.add(y);
              years.add(annoScol);
              years.add(annoScol - 1);
              const arr = Array.from(years).sort((a, b) => b - a);
              return arr.map(y => <option key={y} value={y}>{y}</option>);
            })()}
          </select>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: 9, fontWeight: 800, color: "#4a148c", letterSpacing: 0.4 }}>GIORNI</span>
          <input type="number" min="0" max="31" value={giorniInput}
            onChange={e => setGiorniInput(e.target.value === "" ? "" : Number(e.target.value))}
            onBlur={() => { if (Number(giorniInput) !== Number(data.giorni_scuola || 0)) saveGiorniScuola(); }}
            onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
            data-testid="mensa-mese-giorni-input"
            style={{ height: 36, width: 56, padding: "0 8px", fontSize: 15, fontWeight: 800, border: "2px solid #6a1b9a", background: "white", color: "#4a148c", fontFamily: "monospace", textAlign: "center", boxSizing: "border-box" }} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: 9, fontWeight: 800, color: "#e65100", letterSpacing: 0.4 }} title="Intervallo di date entro cui sono state totalizzate le presenze in mensa per questo mese">📅 PERIODO PRES.</span>
          <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
            <input type="date" value={dataDa || ""}
              onChange={e => setDataDa(e.target.value)}
              onBlur={() => { if ((dataDa || "") !== (data.data_da || "")) savePeriodoDate(); }}
              data-testid="mensa-mese-data-da"
              title="Data iniziale del periodo presenze"
              style={{ height: 36, padding: "0 4px", fontSize: 11, fontWeight: 700, border: "2px solid #ef6c00", background: "#fff3e0", color: "#bf360c", fontFamily: "monospace", boxSizing: "border-box" }} />
            <span style={{ fontSize: 12, fontWeight: 800, color: "#e65100" }}>→</span>
            <input type="date" value={dataA || ""}
              onChange={e => setDataA(e.target.value)}
              onBlur={() => { if ((dataA || "") !== (data.data_a || "")) savePeriodoDate(); }}
              data-testid="mensa-mese-data-a"
              title="Data finale del periodo presenze"
              style={{ height: 36, padding: "0 4px", fontSize: 11, fontWeight: 700, border: "2px solid #ef6c00", background: "#fff3e0", color: "#bf360c", fontFamily: "monospace", boxSizing: "border-box" }} />
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }} data-testid="mensa-mese-flag-filter">
          <span style={{ fontSize: 9, fontWeight: 800, color: "#4a148c", letterSpacing: 0.4 }}>FILTRO FLAG</span>
          <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
            <select value={flagTab} onChange={e => setFlagTab(e.target.value)} data-testid="mensa-mese-flag-select"
              style={{ height: 36, padding: "0 8px", fontSize: 12, fontWeight: 800, border: "2px solid #6a1b9a", background: "white", color: "#4a148c", minWidth: 170, cursor: "pointer", textTransform: "uppercase", letterSpacing: 0.2, boxSizing: "border-box" }}>
              {[
                { k: "tutti",         l: "Tutto" },
                { k: "esente",        l: "Esente" },
                { k: "privato",       l: "Privato" },
                { k: "fratelli",      l: "Fratelli" },
                { k: "agevolato",     l: "Agevolati" },
                { k: "non_residente", l: "Non Residente" },
                { k: "addebito_zero", l: "Addebito €0" },
                { k: "divergenti_segremat", l: "Divergenti Segr." },
              ].map(o => (
                <option key={o.k} value={o.k}>{o.l} ({flagCounts[o.k] || 0})</option>
              ))}
            </select>
            {flagTab !== "tutti" && (
              <button onClick={() => setFlagTab("tutti")} data-testid="mensa-mese-flag-reset"
                title="Azzera filtro"
                style={{ height: 36, padding: "0 8px", background: "white", color: "#4a148c", border: "2px solid #6a1b9a", fontSize: 11, fontWeight: 800, cursor: "pointer", lineHeight: 1 }}>✕</button>
            )}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 160, flex: "1 1 160px" }}>
          <span style={{ fontSize: 9, fontWeight: 800, color: "#4a148c", letterSpacing: 0.4 }}>CERCA</span>
          <div style={{ position: "relative", display: "flex", alignItems: "stretch", height: 36 }}>
            <input type="text" placeholder="Cognome, nome, CF…"
              value={search} onChange={e => setSearch(e.target.value)}
              data-testid="mensa-mese-search-header"
              style={{ flex: 1, height: 36, padding: "0 28px 0 10px", fontSize: 12, fontWeight: 700, border: "2px solid #6a1b9a", background: "white", color: "#4a148c", boxSizing: "border-box" }} />
            {search && (
              <button onClick={() => setSearch("")} data-testid="mensa-mese-search-clear"
                title="Pulisci ricerca"
                style={{ position: "absolute", right: 2, top: "50%", transform: "translateY(-50%)", padding: "2px 6px", background: "transparent", color: "#6a1b9a", border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer", lineHeight: 1 }}>✕</button>
            )}
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <input type="file" accept=".xls,.xlsx,.xml" ref={fileInputRef} onChange={onFileSelected} style={{ display: "none" }} data-testid="mensa-mese-file-input" />
        <button onClick={handleAggiorna} disabled={aggiornando} data-testid="mensa-mese-aggiorna-btn"
          title="Ricalcola i valori in base ai flag attuali ed evidenzia con ! i valori variati"
          style={{
            height: 36, padding: "0 14px",
            background: aggiornando ? "#90a4ae" : "linear-gradient(180deg, #2e7d32 0%, #1b5e20 100%)",
            color: "white",
            border: "2px solid #1b5e20",
            fontSize: 12, fontWeight: 800, letterSpacing: 0.3,
            cursor: aggiornando ? "wait" : "pointer",
            boxShadow: "0 2px 6px rgba(27,94,32,0.4)",
            textTransform: "uppercase",
            display: "flex", alignItems: "center", gap: 6,
          }}>
          {aggiornando ? "Aggiorno..." : "🔄 Aggiorna"}
        </button>
        <button onClick={handleAllinea} disabled={allineando || allineabili.length === 0} data-testid="mensa-mese-allinea-btn"
          title="Allinea gli addebiti Segremat esistenti (non pagati e diversi dal calcolo) al valore calcolato dalla tabella"
          style={{
            height: 36, padding: "0 14px",
            background: (allineando || allineabili.length === 0) ? "#b0bec5" : "linear-gradient(180deg, #ef6c00 0%, #e65100 100%)",
            color: "white",
            border: `2px solid ${(allineando || allineabili.length === 0) ? "#90a4ae" : "#e65100"}`,
            fontSize: 12, fontWeight: 800, letterSpacing: 0.3,
            cursor: (allineando || allineabili.length === 0) ? "default" : "pointer",
            boxShadow: (allineando || allineabili.length === 0) ? "none" : "0 2px 6px rgba(230,81,0,0.4)",
            textTransform: "uppercase",
            display: "flex", alignItems: "center", gap: 6,
          }}>
          {allineando ? "Allineo..." : `🔁 Allinea Segr. (${allineabili.length})`}
        </button>
        <button onClick={handleSyncSegremat} disabled={syncing} data-testid="mensa-mese-sync-segremat-btn"
          title="Scarica da Segremat i movimenti aggiornati (necessario per vedere addebiti di nuovi mesi o creati/modificati direttamente su Segremat)"
          style={{
            height: 36, padding: "0 14px",
            background: syncing ? "#90a4ae" : "linear-gradient(180deg, #0277bd 0%, #01579b 100%)",
            color: "white",
            border: "2px solid #01579b",
            fontSize: 12, fontWeight: 800, letterSpacing: 0.3,
            cursor: syncing ? "wait" : "pointer",
            boxShadow: syncing ? "none" : "0 2px 6px rgba(1,87,155,0.4)",
            textTransform: "uppercase",
            display: "flex", alignItems: "center", gap: 6,
          }}>
          {syncing ? "Sincronizzo..." : "⬇️ Sync Segremat"}
        </button>
        <button onClick={() => fileInputRef.current?.click()} disabled={importing} data-testid="mensa-mese-import-btn"
          style={{
            height: 36, padding: "0 14px",
            background: importing ? "#90a4ae" : "linear-gradient(180deg, #6a1b9a 0%, #4a148c 100%)",
            color: "white",
            border: "2px solid #2c0064",
            fontSize: 12, fontWeight: 800, letterSpacing: 0.3,
            cursor: importing ? "wait" : "pointer",
            boxShadow: "0 2px 6px rgba(74,20,140,0.4)",
            textTransform: "uppercase",
            display: "flex", alignItems: "center", gap: 6,
          }}>
          {importing ? "Importazione..." : `📤 Importa`}
        </button>
        <button onClick={handlePrint} data-testid="mensa-mese-print-btn"
          title="Stampa lista presenze: una pagina per classe + totali"
          style={{
            height: 36, padding: "0 10px",
            background: "white",
            color: "#1565c0",
            border: "2px solid #1565c0",
            fontSize: 11, fontWeight: 800, cursor: "pointer",
            display: "flex", alignItems: "center", gap: 4, letterSpacing: 0.3, textTransform: "uppercase",
          }}>🖨 Stampa</button>
        <button onClick={() => setParamsOpen(true)} data-testid="mensa-mese-params-btn"
          title="Parametri di gestione (costi pasto, sconti, rette)"
          style={{
            height: 36, padding: "0 10px",
            background: "white",
            color: "#4a148c",
            border: "2px solid #6a1b9a",
            fontSize: 18, fontWeight: 800, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>⚙</button>
      </div>

      {/* Stats riga */}
      <div style={{ margin: "0 20px 8px", display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <div data-testid="mensa-mese-stats"
          style={{ padding: "8px 14px", background: isImported ? "#e8f5e9" : "#fffde7", border: `1px solid ${isImported ? "#66bb6a" : "#ffa726"}`, fontSize: 13, fontWeight: 700, color: isImported ? "#1b5e20" : "#e65100" }}>
          {isImported
            ? <>📊 {MESI[mese - 1]} {anno}: <b>{data.totale_presenze}</b> presenze · <b>{filtered.length}</b> alunni mostrati su <b>{data.totale_alunni}</b> · ISEE anno <b>{data.anno_isee || annoIsee || "—"}</b> · <b>Totale €{(data.totale_valore || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b></>
            : <>⚠️ Nessun dato importato per {MESI[mese - 1]} {anno}. Usa il pulsante Importa.</>
          }
        </div>
        {segMatched > 0 && (
          <div data-testid="mensa-mese-diff-count"
            onClick={() => { if (diffCount > 0) setFlagTab(flagTab === "divergenti_segremat" ? "tutti" : "divergenti_segremat"); }}
            title={diffCount > 0 ? "Clicca per mostrare solo le rette con importo diverso da Segremat (clicca di nuovo per azzerare il filtro)" : "Numero di righe il cui importo reale su Segremat è diverso dal calcolo"}
            style={{ padding: "8px 14px", display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 800,
              cursor: diffCount > 0 ? "pointer" : "default",
              background: diffCount > 0 ? "#fff3e0" : "#e8f5e9",
              border: `${flagTab === "divergenti_segremat" ? "2px" : "1px"} solid ${diffCount > 0 ? "#ef6c00" : "#66bb6a"}`,
              outline: flagTab === "divergenti_segremat" ? "2px solid #e65100" : "none",
              color: diffCount > 0 ? "#e65100" : "#1b5e20" }}>
            {diffCount > 0 ? (
              <>
                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, borderRadius: "50%", background: "#ef6c00", color: "white", fontWeight: 900, fontSize: 13, flexShrink: 0 }}>!</span>
                <span><b data-testid="mensa-mese-diff-count-n">{diffCount}</b> {diffCount === 1 ? "retta diverge" : "rette divergono"} da Segremat{flagTab === "divergenti_segremat" ? " — ✕ filtro attivo" : ""}</span>
              </>
            ) : (
              <span>✓ Importi allineati a Segremat (<b data-testid="mensa-mese-diff-count-n">{segMatched}</b>)</span>
            )}
          </div>
        )}
      </div>

      {/* Barra creazione addebiti Segremat */}
      {isImported && (
        <div style={{ margin: "0 20px 8px", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", padding: "8px 12px", background: "#ede7f6", border: "1px solid #b39ddb", borderRadius: 4 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#4a148c" }}>
            🧾 Selezionati: <b data-testid="mm-addebiti-selcount">{selCount}</b> / {selectableAddebiti.length} alunni (righe a €0 escluse: Segremat non le accetta)
          </span>
          <button data-testid="mm-crea-addebiti-btn" onClick={openAddebitiModal} disabled={selCount === 0}
            title={selCount === 0 ? "Spunta almeno un alunno" : "Crea gli addebiti su Segremat per gli alunni selezionati"}
            style={{ padding: "7px 16px", background: selCount === 0 ? "#bdbdbd" : "#4a148c", color: "white", border: "none", borderRadius: 3, fontSize: 13, fontWeight: 800, cursor: selCount === 0 ? "not-allowed" : "pointer", letterSpacing: 0.3 }}>
            💳 Crea addebiti Segremat
          </button>
          {selCount > 0 && (
            <button data-testid="mm-addebiti-clear" onClick={() => setSelAddebiti(new Set())}
              style={{ padding: "6px 12px", background: "white", color: "#4a148c", border: "1px solid #b39ddb", borderRadius: 3, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              Deseleziona tutti
            </button>
          )}
        </div>
      )}
      <div style={{ margin: "0 20px 8px", display: "flex", gap: 6, flexWrap: "wrap", alignItems: "stretch", borderBottom: "3px solid #1565c0", paddingBottom: 0 }} data-testid="mensa-mese-settore-tabs">
        {[
          { k: "tutti",    l: "Tutti",            c: "#37474f", bg: "#eceff1" },
          { k: "A",        l: "A · Infanzia",     c: "#e91e63", bg: "#fce4ec" },
          { k: "E",        l: "E · Primaria E",   c: "#2e7d32", bg: "#e8f5e9" },
          { k: "F",        l: "F · Primaria F",   c: "#558b2f", bg: "#f1f8e9" },
          { k: "M",        l: "M · Secondaria",   c: "#1565c0", bg: "#e3f2fd" },
          { k: "_ALTRO_",  l: "Altro",            c: "#6a1b9a", bg: "#f3e5f5" },
        ].map(t => {
          const active = settoreTab === t.k;
          const cnt = settoreCounts[t.k] || 0;
          return (
            <button key={t.k} onClick={() => setSettoreTab(t.k)} data-testid={`mensa-mese-tab-settore-${t.k}`}
              title={t.k === "_ALTRO_" ? "Settori non didattici (S, N, …)" : undefined}
              style={{
                padding: "9px 18px",
                background: active ? t.c : t.bg,
                color: active ? "white" : t.c,
                border: `1px solid ${t.c}`,
                borderBottom: active ? `3px solid ${t.c}` : "1px solid transparent",
                fontSize: 13, fontWeight: 800, letterSpacing: 0.3,
                cursor: "pointer",
                textTransform: "uppercase",
                display: "flex", alignItems: "center", gap: 8,
                marginBottom: -3,
                borderTopWidth: 3,
              }}>
              <span>{t.l}</span>
              <span style={{
                padding: "1px 8px",
                background: active ? "rgba(255,255,255,0.25)" : t.c,
                color: "white",
                fontSize: 11, fontWeight: 800,
                minWidth: 26, textAlign: "center",
                borderRadius: 2,
              }}>{cnt}</span>
            </button>
          );
        })}
      </div>

      {/* Legenda evidenziazioni */}
      <div style={{ margin: "0 20px 8px", padding: "6px 12px", background: "white", border: "1px solid #ddd", display: "flex", gap: 16, fontSize: 11, color: "#37474f", alignItems: "center", flexWrap: "wrap" }}>
        <b style={{ color: "#4a148c", letterSpacing: 0.5 }}>LEGENDA:</b>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ display: "inline-block", width: 14, height: 14, background: "#aed581", border: "2px solid #33691e" }}></span>
          <b style={{ color: "#1b5e20" }}>ISEE valorizzato o Agevolato</b>
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ display: "inline-block", width: 14, height: 14, background: "#ff8a80", border: "2px solid #b71c1c" }}></span>
          <b style={{ color: "#b71c1c" }}>Presenze a zero</b>
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ display: "inline-block", width: 14, height: 14, background: "#ffe0b2", border: "2px solid #ef6c00" }}></span>
          <b style={{ color: "#e65100" }}>Alunno orfano (CF non in anagrafica)</b>
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 16, height: 16, borderRadius: "50%", background: "#d32f2f", color: "white", fontWeight: 900, fontSize: 12, lineHeight: 1 }}>!</span>
          <b style={{ color: "#b71c1c" }}>Valore variato dopo l'aggiornamento</b>
        </span>
      </div>

      {/* Periodi importati */}
      {periodi.length > 0 && (
        <div style={{ margin: "0 20px 12px", padding: 8, background: "white", border: "1px solid #cfd8dc", fontSize: 11 }}>
          <span style={{ color: "#666", fontWeight: 700 }}>📚 Periodi importati: </span>
          {periodi.map(p => {
            const isActive = (p.anno === anno && p.mese === mese);
            return (
              <button key={`${p.anno}-${p.mese}`} onClick={() => navigate(`/g/mensa_mese/mensa_mese_alunni?anno=${p.anno}&mese=${p.mese}`)}
                data-testid={`mensa-mese-period-${p.anno}-${p.mese}`}
                style={{
                  margin: "0 4px", padding: "2px 8px",
                  background: isActive ? "#6a1b9a" : "#eceff1",
                  color: isActive ? "white" : "#37474f",
                  border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer",
                }}>
                {MESI[p.mese - 1].slice(0, 3)} {p.anno} ({p.alunni}/{p.presenze})
              </button>
            );
          })}
        </div>
      )}

      {/* Tabella */}
      <div style={{ margin: "0 20px 20px", background: "white", border: "1px solid #ccc", overflow: "auto", maxHeight: "70vh" }}>
        <table className="sw-table" style={{ width: "100%" }}>
          <thead>
            <tr>
              <th style={{ width: 46 }} title="Seleziona per creare addebiti Segremat">
                <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "center" }}>
                  <input type="checkbox" data-testid="mm-addebiti-selall" checked={allSelSelected}
                    onChange={toggleSelAll} title="Seleziona/deseleziona tutti" style={{ cursor: "pointer" }} />
                  <span>#</span>
                </div>
              </th>
              <th>Cod</th>
              <th style={{ width: 130 }}>Cognome</th>
              <th style={{ width: 120 }}>Nome</th>
              <th style={{ width: 200 }}>Codice Fiscale</th>
              <th style={{ width: 60 }}>Sett.</th>
              <th style={{ width: 80 }}>Classe</th>
              <th style={{ width: 150, background: "#e0f7fa" }} title="ISEE — solo per alunni Infanzia (settore A)">ISEE</th>
              <th style={{ width: 110, background: "#e1f5fe", color: "#01579b" }} title="Percentuale contribuzione = 34 + (ISEE − 0) × (100 − 34) / (32.000 − 0), clamp [34%, 100%]. Solo per Infanzia.">% Contrib.</th>
              <th style={{ width: 130, background: "#e1f5fe", color: "#01579b", fontSize: 14 }} title="Retta base mensile (solo Infanzia A): retta base × % calcolata da ISEE, oppure retta privati/non residenti senza sconti">Retta base €</th>
              <th style={{ width: 80, background: "#f3e5f5" }}>Presenze</th>
              <th style={{ width: 80, background: "#ffebee" }} title={data.giorni_scuola ? `Giorni scuola - presenze (${data.giorni_scuola}-presenze)` : "Imposta i giorni scuola per calcolare le assenze"}>Assenze</th>
              <th style={{ width: 50, background: "#fff8e1" }} title="Esente">Es.</th>
              <th style={{ width: 50, background: "#fff8e1" }} title="Privato">Priv.</th>
              <th style={{ width: 50, background: "#fff8e1" }} title="Fratelli">Fr.</th>
              <th style={{ width: 50, background: "#fff8e1" }} title="Agevolato">Agev.</th>
              <th style={{ width: 50, background: "#fff8e1" }} title="Non residente">N.R.</th>
              <th style={{ width: 150, background: "#ede7f6", color: "#4a148c", fontSize: 14 }} title="Valore mensa mese calcolato dai parametri di gestione (presenze × tariffa, applicando agevolazioni e esenzioni)">Valore €</th>
              <th style={{ width: 160, background: "#e8f5e9", color: "#1b5e20", fontSize: 14 }} title="Importo REALE dell'addebito Mensa Mese già creato su Segremat (letto dai Movimenti sincronizzati). Cliccabile per modificarlo: la modifica viene scritta direttamente su Segremat.">Importo Segr. €</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={19} className="sw-loading">Caricamento...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={19} className="sw-empty"><p>{isImported ? "Nessun alunno corrisponde ai filtri." : "Nessun dato. Importa il file."}</p></td></tr>
            ) : (() => {
              // Raggruppa per Plesso (settore) → Classe
              const rows = [];
              let curPlesso = null;
              let curClasse = null;
              let plessoBuf = null;
              let classeBuf = null;

              const flushClasse = () => {
                if (!classeBuf) return;
                rows.push({ kind: "subtot-classe", ...classeBuf });
                classeBuf = null;
              };
              const flushPlesso = () => {
                if (!plessoBuf) return;
                rows.push({ kind: "subtot-plesso", ...plessoBuf });
                plessoBuf = null;
              };

              filtered.forEach((a, i) => {
                const ples = a.settore || "—";
                const cls = a.classe || "—";
                if (ples !== curPlesso) {
                  flushClasse();
                  flushPlesso();
                  curPlesso = ples;
                  curClasse = null;
                  plessoBuf = { plesso: ples, alunni: 0, presenze: 0, assenze: 0, valore: 0, importoSegr: 0 };
                  rows.push({ kind: "header-plesso", plesso: ples });
                }
                if (cls !== curClasse) {
                  flushClasse();
                  curClasse = cls;
                  classeBuf = { plesso: ples, classe: cls, alunni: 0, presenze: 0, assenze: 0, valore: 0, importoSegr: 0 };
                  rows.push({ kind: "header-classe", plesso: ples, classe: cls });
                }
                rows.push({ kind: "row", a, i });
                const pres = Number(a.presenze || 0);
                const ass = data.giorni_scuola > 0 ? Number(a.assenze || 0) : 0;
                const val = Number(a.valore_mensa_mese || 0);
                const impS = Number(a.importo_segremat || 0);
                classeBuf.alunni += 1;
                classeBuf.presenze += pres;
                classeBuf.assenze += ass;
                classeBuf.valore += val;
                classeBuf.importoSegr += impS;
                plessoBuf.alunni += 1;
                plessoBuf.presenze += pres;
                plessoBuf.assenze += ass;
                plessoBuf.valore += val;
                plessoBuf.importoSegr += impS;
              });
              flushClasse();
              flushPlesso();

              let visibleIdx = 0;
              return rows.map((r, ri) => {
                if (r.kind === "header-plesso") {
                  return (
                    <tr key={`hp-${ri}`} data-testid={`mensa-mese-plesso-${r.plesso}`} style={{ background: "#4a148c" }}>
                      <td colSpan={19} style={{ color: "white", fontWeight: 800, fontSize: 14, padding: "8px 12px", letterSpacing: 0.5, textTransform: "uppercase" }}>
                        🏫 PLESSO {r.plesso}
                      </td>
                    </tr>
                  );
                }
                if (r.kind === "header-classe") {
                  return (
                    <tr key={`hc-${ri}`} data-testid={`mensa-mese-classe-${r.classe}`} style={{ background: "#e1bee7" }}>
                      <td colSpan={19} style={{ color: "#4a148c", fontWeight: 700, fontSize: 12, padding: "5px 18px", letterSpacing: 0.3 }}>
                        ▸ Classe {r.classe}
                      </td>
                    </tr>
                  );
                }
                if (r.kind === "subtot-classe") {
                  return (
                    <tr key={`sc-${ri}`} style={{ background: "#f3e5f5", borderTop: "2px solid #ce93d8" }}>
                      <td colSpan={6} style={{ textAlign: "right", padding: "5px 8px", fontStyle: "italic", color: "#4a148c", fontWeight: 700, fontSize: 11 }}>
                        Subtotale {r.classe} ({r.alunni} alunni)
                      </td>
                      <td colSpan={2} style={{ textAlign: "right", padding: "5px 8px", color: "#4a148c", fontWeight: 700, fontSize: 11 }}>—</td>
                      <td style={{ background: "#f3e5f5" }}></td>
                      <td style={{ background: "#f3e5f5" }}></td>
                      <td className="num-col" style={{ background: "#f3e5f5", fontWeight: 800, color: "#4a148c", fontFamily: "monospace", fontSize: 13 }}>{r.presenze}</td>
                      <td className="num-col" style={{ background: "#fff5f5", fontWeight: 800, color: "#c62828", fontFamily: "monospace", fontSize: 13 }}>
                        {data.giorni_scuola > 0 ? r.assenze : "—"}
                      </td>
                      <td colSpan={5} style={{ background: "#f3e5f5" }}></td>
                      <td className="num-col" style={{ background: "#ede7f6", fontWeight: 900, color: "#4a148c", fontFamily: "monospace", fontSize: 15, textAlign: "right", paddingRight: 10 }}
                        data-testid={`mensa-mese-subtot-classe-valore-${r.classe}`}>
                        € {Number(r.valore || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="num-col" style={{ background: "#e8f5e9", fontWeight: 900, color: "#1b5e20", fontFamily: "monospace", fontSize: 15, textAlign: "right", paddingRight: 10 }}
                        data-testid={`mensa-mese-subtot-classe-importo-segremat-${r.classe}`}>
                        € {Number(r.importoSegr || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  );
                }
                if (r.kind === "subtot-plesso") {
                  return (
                    <tr key={`sp-${ri}`} style={{ background: "#9c27b0", borderTop: "3px solid #4a148c", borderBottom: "3px solid #4a148c" }}>
                      <td colSpan={6} style={{ textAlign: "right", padding: "8px 12px", color: "white", fontWeight: 800, fontSize: 12, letterSpacing: 0.3 }}>
                        TOTALE PLESSO {r.plesso} ({r.alunni} alunni)
                      </td>
                      <td colSpan={2} style={{ textAlign: "right", padding: "8px 12px", color: "white", fontWeight: 800, fontSize: 12 }}>—</td>
                      <td style={{ background: "#9c27b0" }}></td>
                      <td style={{ background: "#9c27b0" }}></td>
                      <td className="num-col" style={{ background: "#7b1fa2", fontWeight: 900, color: "white", fontFamily: "monospace", fontSize: 14 }}>{r.presenze}</td>
                      <td className="num-col" style={{ background: "#7b1fa2", fontWeight: 900, color: "white", fontFamily: "monospace", fontSize: 14 }}>
                        {data.giorni_scuola > 0 ? r.assenze : "—"}
                      </td>
                      <td colSpan={5} style={{ background: "#9c27b0" }}></td>
                      <td className="num-col" style={{ background: "#4a148c", fontWeight: 900, color: "white", fontFamily: "monospace", fontSize: 16, textAlign: "right", paddingRight: 10 }}
                        data-testid={`mensa-mese-subtot-plesso-valore-${r.plesso}`}>
                        € {Number(r.valore || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="num-col" style={{ background: "#1b5e20", fontWeight: 900, color: "white", fontFamily: "monospace", fontSize: 16, textAlign: "right", paddingRight: 10 }}
                        data-testid={`mensa-mese-subtot-plesso-importo-segremat-${r.plesso}`}>
                        € {Number(r.importoSegr || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  );
                }
                // row
                const a = r.a;
                visibleIdx += 1;
                // Evidenziazioni MARCATE:
                // - Verde forte se ha ISEE o flag Agevolato
                // - Rosso forte se presenze = 0 (ma ha CF noto)
                // - Arancio per orfani (CF non in anagrafica)
                const hasIseeOrAgev = (a.isee != null && a.isee !== "" && Number(a.isee) > 0) || a.agevolato;
                const presZero = (Number(a.presenze) || 0) === 0;
                const rowClass = hasIseeOrAgev ? "mensa-row-isee"
                                : presZero ? "mensa-row-zero"
                                : a.orphan ? "mensa-row-orphan"
                                : "";
                const leftBar = hasIseeOrAgev ? "10px solid #33691e"
                              : presZero ? "10px solid #b71c1c"
                              : a.orphan ? "10px solid #ef6c00"
                              : "10px solid transparent";
                const rowFontWeight = (hasIseeOrAgev || presZero || a.orphan) ? 800 : undefined;
                return (
                  <tr key={`${a.Cod || a.CodFis}-${r.i}`} data-testid={`mensa-mese-row-${r.i}`} className={rowClass} style={{ fontWeight: rowFontWeight }}>
                    <td className="center-col" style={{ color: "#888", borderLeft: leftBar }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "center" }}>
                        {((a.settore || "").toUpperCase().split(",").some(s => ["A", "E", "F", "M"].includes(s.trim())) && Number(a.valore_mensa_mese || 0) > 0)
                          ? <input type="checkbox" data-testid={`mm-addebiti-chk-${a.Cod || a.CodFis}`}
                              checked={selAddebiti.has(a.Cod || a.CodFis)} onChange={() => toggleSelOne(a.Cod || a.CodFis)}
                              title="Seleziona per addebito" style={{ cursor: "pointer" }} />
                          : <span style={{ width: 13, display: "inline-block" }} title={Number(a.valore_mensa_mese || 0) === 0 ? "Importo €0: Segremat non accetta addebiti a importo zero" : undefined} />}
                        <span>{visibleIdx}</span>
                      </div>
                    </td>
                    <td>{a.Cod
                      ? <button onClick={() => navigate(`/g/${gestione}/movimenti?codice_alunno=${a.Cod}&anno=${paramAnno}`)}
                          title="Apri movimenti di questo alunno"
                          data-testid={`mm-mov-${a.Cod}`}
                          style={{ background: "none", border: "none", color: "#1565c0", textDecoration: "underline", fontWeight: 700, cursor: "pointer", padding: 0, fontSize: "inherit" }}>
                          {a.Cod}
                        </button>
                      : <span style={{ color: "#ef6c00", fontWeight: 700 }} title="CF non trovato negli alunni">⚠</span>}
                    </td>
                    <td style={{ fontWeight: 600, maxWidth: 130, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={a.Cog}>{a.Cog}</td>
                    <td style={{ maxWidth: 120, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={a.Nom}>{a.Nom}</td>
                    <td style={{ fontSize: 11, fontFamily: "Consolas,monospace" }}>{a.CodFis || "-"}</td>
                    <td className="center-col">{a.settore || "-"}</td>
                    <td>{a.classe || "-"}</td>
                    {(() => {
                      const settList = (a.settore || "").toUpperCase().split(",").map(s => s.trim()).filter(Boolean);
                      const isInfanzia = settList.includes("A");
                      return (
                        <>
                          <td style={{ background: isInfanzia ? (a.isee != null ? "#e0f7fa" : "#fafafa") : "#f5f5f5", padding: 2, position: "relative" }}
                            title={!isInfanzia ? "ISEE non applicabile (solo Infanzia)" : (a.isee != null ? `ISEE anno ${a.isee_anno}` : "Nessun ISEE registrato per quest'AS")}>
                            {isInfanzia ? (
                              <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <div style={{ flex: 1 }}>
                                  <IseeInput alunno={a} onCommit={(v) => setIseeAlunno(a, v)} />
                                </div>
                                {a.CodFis && (
                                  <button onClick={async () => {
                                    try {
                                      const r = await api.get(`/mensa-mese/isee/by-cf/${a.CodFis}`);
                                      setIseeModal({ alunno: a, list: r.data?.data || [] });
                                    } catch (e) {
                                      alert("Errore: " + (e.response?.data?.detail || e.message));
                                    }
                                  }} data-testid={`mm-isee-open-${a.CodFis}`}
                                    title="Apri storico ISEE dell'alunno"
                                    style={{ padding: "1px 5px", background: "#00838f", color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>📋</button>
                                )}
                              </div>
                            ) : (
                              <span style={{ color: "#bbb", fontStyle: "italic", fontSize: 11 }}>—</span>
                            )}
                          </td>
                          {/* 🆕 % Contribuzione = 34 + ISEE × (100−34) / (32000−0), clamp [34%, 100%]. Solo per Infanzia. */}
                          {(() => {
                            let label = "—", color = "#bbb", bg = isInfanzia ? "#fafafa" : "#f5f5f5", tip = !isInfanzia ? "% contribuzione: solo per Infanzia (A)" : "ISEE non presente → percentuale non calcolabile";
                            if (isInfanzia && a.isee != null && a.isee !== "") {
                              const n = Number(a.isee);
                              if (Number.isFinite(n)) {
                                let pct = 34 + n * (100 - 34) / 32000;
                                if (pct < 34) pct = 34;
                                if (pct > 100) pct = 100;
                                const isMin = pct <= 34;
                                const isMax = pct >= 100;
                                label = pct.toFixed(2).replace(".", ",") + " %";
                                color = isMax ? "#c62828" : (isMin ? "#1b5e20" : "#0d47a1");
                                bg = isMax ? "#ffebee" : (isMin ? "#e8f5e9" : "#e3f2fd");
                                tip = `ISEE € ${n.toLocaleString("it-IT", { minimumFractionDigits: 2 })} → ${pct.toFixed(2)}%${isMin ? " (minimo)" : isMax ? " (massimo)" : ""}`;
                              }
                            }
                            return (
                              <td data-testid={`mensa-mese-pct-${a.CodFis || a.Cod}`} title={tip}
                                style={{ textAlign: "center", background: bg, fontFamily: "Consolas, monospace", fontWeight: 800, color, fontSize: 12 }}>
                                {label}
                              </td>
                            );
                          })()}
                          <td className="mensa-num-big" style={{
                            background: isInfanzia ? "#e1f5fe" : "#f5f5f5",
                            fontFamily: "monospace", fontWeight: 800, color: "#01579b",
                            textAlign: "right", paddingRight: 10, letterSpacing: 0.3, lineHeight: 1.1,
                          }}
                          data-testid={`mensa-mese-rettabase-${a.CodFis || a.AluCod}`}
                          title={!isInfanzia ? "Retta base solo per Infanzia"
                            : a.privato ? "Privato → retta privati"
                            : a.non_residente ? "Non residente → retta non residenti"
                            : (a.isee == null ? "Residente senza ISEE → retta base infanzia"
                              : `Residente con ISEE ${a.isee} → calcolo percentuale`)}>
                            {isInfanzia && a.retta_base != null ? Number(a.retta_base).toFixed(2)
                              : <span style={{ color: "#bbb", fontSize: 12, fontWeight: 400 }}>—</span>}
                          </td>
                        </>
                      );
                    })()}
                    <td className="num-col mensa-num-big" style={{
                        background: a.presenze ? "#f3e5f5" : "#fafafa",
                        fontWeight: 800, color: a.presenze ? "#4a148c" : "#bbb",
                        fontFamily: "monospace",
                        cursor: a.presenze ? "pointer" : "default",
                        textDecoration: a.presenze ? "underline dotted" : "none",
                      }}
                      onClick={() => { if (a.presenze) setGiorniModal(a); }}
                      data-testid={`mensa-mese-pres-cell-${a.CodFis || a.Cod}`}
                      title={a.presenze ? "Clicca per vedere le giornate di presenza" : ""}>
                      {a.presenze ? a.presenze : "0"}
                    </td>
                    <td className="num-col mensa-num-big" style={{ background: a.assenze > 0 ? "#ffebee" : "#fafafa", fontWeight: 800, color: a.assenze > 0 ? "#c62828" : "#bbb", fontFamily: "monospace" }}
                      title={data.giorni_scuola ? `${data.giorni_scuola} giorni scuola - ${a.presenze || 0} presenze` : "Imposta i giorni scuola"}>
                      {data.giorni_scuola > 0 ? (a.assenze ?? 0) : "-"}
                    </td>
                    {["esente", "privato", "fratelli", "agevolato", "non_residente"].map(flag => {
                      const s = (a.settore || "").toUpperCase();
                      const isInfanzia = s.split(",").map(x => x.trim()).includes("A");
                      const isPriSec = ["E", "F", "M"].some(x => s.split(",").map(y => y.trim()).includes(x));
                      // Regole di visibilità delle spunte:
                      // - Infanzia (A): nascondi "agevolato" (non si applica)
                      // - Primaria/Secondaria (E/F/M): nascondi "privato", "fratelli", "non_residente"
                      const hide = (isInfanzia && flag === "agevolato")
                                || (isPriSec && ["privato", "fratelli", "non_residente"].includes(flag));
                      return (
                        <td key={flag} className="center-col" style={{ background: "#fff8e1", padding: 0 }}>
                          {!hide && (
                            <input type="checkbox" checked={!!a[flag]} onChange={() => toggleFlag(a, flag)}
                              data-testid={`mensa-mese-flag-${flag}-${a.CodFis}`}
                              disabled={!a.CodFis}
                              style={{ width: 18, height: 18, cursor: a.CodFis ? "pointer" : "not-allowed", accentColor: "#6a1b9a" }} />
                          )}
                        </td>
                      );
                    })}
                    <td className="center-col mensa-valore-big" style={{ background: "#ede7f6", fontFamily: "monospace", fontWeight: 800, color: "#4a148c", textAlign: "right", paddingRight: 10, letterSpacing: 0.3, lineHeight: 1.1 }}
                        data-testid={`mensa-mese-valore-${a.CodFis || a.AluCod}`}
                        title={(() => {
                          const s = (a.settore || "").toUpperCase();
                          if (a.esente) return "Esente: valore 0";
                          if (s.includes("A")) return "Infanzia: logica complessa (TBD)";
                          if (s.includes("E") || s.includes("F") || s.includes("M")) {
                            return `Presenze ${a.presenze || 0} × tariffa ${a.agevolato ? "agevolata" : (s.includes("M") ? "secondaria" : "primaria")}`;
                          }
                          return "Settore non gestito per il calcolo";
                        })()}>
                      <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 5 }}>
                        {changedVals.has(rowKey(a)) && (
                          <span data-testid={`mensa-mese-valore-changed-${a.CodFis || a.Cod}`}
                            title="Valore variato dopo l'aggiornamento"
                            style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 18, height: 18, borderRadius: "50%", background: "#d32f2f", color: "white", fontWeight: 900, fontSize: 13, lineHeight: 1, flexShrink: 0 }}>!</span>
                        )}
                        <span>{a.valore_mensa_mese != null ? Number(a.valore_mensa_mese).toFixed(2) : "—"}</span>
                      </span>
                    </td>
                    {(() => {
                      const impSeg = a.importo_segremat;
                      const hasMov = impSeg != null && a.codice_movimento_segremat != null;
                      const diff = hasMov && Math.abs(Number(impSeg) - Number(a.valore_mensa_mese || 0)) > 0.001;
                      const editing = editMovKey != null && editMovKey === a.codice_movimento_segremat;
                      const bg = diff ? "#fff3e0" : "#e8f5e9";
                      return (
                        <td className="center-col" style={{ background: bg, fontFamily: "monospace", fontWeight: 800, color: diff ? "#e65100" : "#1b5e20", textAlign: "right", paddingRight: 8, letterSpacing: 0.3, lineHeight: 1.1, whiteSpace: "nowrap" }}
                          data-testid={`mensa-mese-importo-segremat-${a.CodFis || a.Cod}`}
                          title={!hasMov ? "Nessun addebito Mensa Mese trovato su Segremat per questo mese" : (a.pagato_segremat ? "Addebito già pagato: importo non modificabile" : (diff ? `Addebito reale su Segremat (diverso dal calcolo di € ${Number(a.valore_mensa_mese || 0).toFixed(2)}). Clicca ✎ per modificarlo su Segremat.` : "Addebito reale su Segremat. Clicca ✎ per modificarlo."))}>
                          {!hasMov ? (
                            <span style={{ color: "#bbb" }}>—</span>
                          ) : editing ? (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 3, justifyContent: "flex-end" }}>
                              <input type="number" step="0.01" min="0" value={editMovVal} autoFocus
                                data-testid={`mensa-mese-importo-segremat-input-${a.CodFis || a.Cod}`}
                                onChange={e => setEditMovVal(e.target.value)}
                                onKeyDown={e => { if (e.key === "Enter") saveImporto(a); if (e.key === "Escape") cancelEditImporto(); }}
                                style={{ width: 72, height: 26, padding: "0 4px", fontSize: 12, fontWeight: 800, textAlign: "right", border: "2px solid #1b5e20", fontFamily: "monospace" }} />
                              <button onClick={() => saveImporto(a)} disabled={savingImporto}
                                data-testid={`mensa-mese-importo-segremat-save-${a.CodFis || a.Cod}`}
                                title="Salva su Segremat" style={{ height: 26, width: 26, background: "#2e7d32", color: "white", border: "none", cursor: savingImporto ? "wait" : "pointer", fontWeight: 900, fontSize: 13, lineHeight: 1 }}>✓</button>
                              <button onClick={cancelEditImporto} disabled={savingImporto}
                                title="Annulla" style={{ height: 26, width: 26, background: "#c62828", color: "white", border: "none", cursor: "pointer", fontWeight: 900, fontSize: 13, lineHeight: 1 }}>✕</button>
                            </span>
                          ) : (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, justifyContent: "flex-end" }}>
                              {diff && <span title="Diverso dal calcolo" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 18, height: 18, borderRadius: "50%", background: "#ef6c00", color: "white", fontWeight: 900, fontSize: 12, lineHeight: 1, flexShrink: 0 }}>!</span>}
                              <span>{Number(impSeg).toFixed(2)}</span>
                              {a.pagato_segremat ? (
                                <span title="Pagato: non modificabile" style={{ fontSize: 12 }}>🔒</span>
                              ) : (
                                <button onClick={() => startEditImporto(a)}
                                  data-testid={`mensa-mese-importo-segremat-edit-${a.CodFis || a.Cod}`}
                                  title="Modifica importo su Segremat" style={{ background: "none", border: "none", cursor: "pointer", color: "#1b5e20", fontSize: 13, padding: 0, lineHeight: 1 }}>✎</button>
                              )}
                            </span>
                          )}
                        </td>
                      );
                    })()}
                  </tr>
                );
              });
            })()}
          </tbody>
        </table>
      </div>
      {iseeModal && (
        <IseeStoricoModal data={iseeModal} onClose={() => setIseeModal(null)}
          onChanged={async () => {
            // Refresh list dell'alunno e dati mensa
            try {
              const r = await api.get(`/mensa-mese/isee/by-cf/${iseeModal.alunno.CodFis}`);
              setIseeModal(prev => prev ? { ...prev, list: r.data?.data || [] } : null);
            } catch { /* ignore */ }
            fetchData();
          }} />
      )}
      {giorniModal && (
        <GiorniPresenzaModal alunno={giorniModal} mese={mese} anno={anno}
          onClose={() => setGiorniModal(null)} />
      )}
      {paramsOpen && <MensaParamsModal annoScol={annoScol} onClose={() => setParamsOpen(false)} />}
      {logOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={() => setLogOpen(false)} data-testid="mensa-mese-log-modal">
          <div onClick={e => e.stopPropagation()}
            style={{ background: "white", width: "min(1100px, 96vw)", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 10px 30px rgba(0,0,0,0.3)" }}>
            <div style={{ background: "#bf360c", color: "white", padding: "12px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: 0.4 }}>📝 STORICO MODIFICHE SPUNTE</div>
                <div style={{ fontSize: 11, opacity: 0.9, marginTop: 2 }}>{MESI[mese - 1]} {anno} — registro audit di tutti i click su Es./Priv./Fr./Agev./N.R.</div>
              </div>
              <button onClick={() => setLogOpen(false)} data-testid="mensa-mese-log-close"
                style={{ background: "transparent", color: "white", border: "1px solid rgba(255,255,255,0.5)", padding: "4px 14px", cursor: "pointer", fontSize: 14, fontWeight: 700 }}>Chiudi ✕</button>
            </div>
            <div style={{ flex: 1, overflow: "auto", padding: 14 }}>
              {logLoading && <div style={{ padding: 20, textAlign: "center", color: "#888" }}>Caricamento…</div>}
              {!logLoading && logRows.length === 0 && (
                <div style={{ padding: 20, textAlign: "center", color: "#888", border: "1px dashed #ccc" }}>
                  Nessuna modifica registrata per questo mese.
                </div>
              )}
              {!logLoading && logRows.length > 0 && (
                <table className="sw-table" style={{ fontSize: 11, width: "100%" }}>
                  <thead>
                    <tr style={{ background: "#37474f", color: "white" }}>
                      <th style={{ width: 140 }}>DATA / ORA</th>
                      <th style={{ width: 160 }}>COD. FISCALE</th>
                      <th>ALUNNO</th>
                      <th style={{ width: 110, textAlign: "center" }}>FLAG</th>
                      <th style={{ width: 100, textAlign: "center" }}>OPERAZIONE</th>
                      <th style={{ width: 130 }}>UTENTE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logRows.map((r, i) => {
                      const dt = new Date(r.ts);
                      const dtStr = isNaN(dt) ? r.ts : dt.toLocaleString("it-IT", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
                      const FLAG_LABEL = { esente: "Es.", privato: "Priv.", fratelli: "Fr.", agevolato: "Agev.", non_residente: "N.R." };
                      const isSet = r.value_new;
                      const fullName = `${r.cognome || ""} ${r.nome || ""}`.trim() || "—";
                      return (
                        <tr key={`${r.cod_fis}-${r.ts}-${i}`} data-testid={`mensa-mese-log-row-${i}`}
                          style={{ background: r.changed ? "white" : "#fafafa", color: r.changed ? "inherit" : "#888" }}>
                          <td style={{ fontFamily: "monospace", fontSize: 10 }}>{dtStr}</td>
                          <td style={{ fontFamily: "monospace", fontSize: 10, fontWeight: 700 }}>{r.cod_fis}</td>
                          <td>
                            <div style={{ fontWeight: 700, textTransform: "uppercase" }}>{fullName}</div>
                            {(r.classe || r.settore) && (
                              <div style={{ fontSize: 9, color: "#666" }}>
                                {r.classe ? `Cl. ${r.classe}` : ""}{r.classe && r.settore ? " · " : ""}{r.settore ? `Set. ${r.settore}` : ""}
                              </div>
                            )}
                          </td>
                          <td style={{ textAlign: "center", fontWeight: 800, color: "#4a148c" }}>{FLAG_LABEL[r.flag] || r.flag}</td>
                          <td style={{ textAlign: "center", fontWeight: 800, color: isSet ? "#1b5e20" : "#c62828" }}>
                            {isSet ? "✓ ATTIVATO" : "✕ TOLTO"}
                            {!r.changed && <span style={{ display: "block", fontSize: 9, color: "#888", fontWeight: 400 }}>(invariato)</span>}
                          </td>
                          <td style={{ fontSize: 10, color: "#555" }}>
                            <div style={{ fontWeight: 700 }}>{r.user_name || r.user_email || "—"}</div>
                            {r.user_name && r.user_email && <div style={{ fontSize: 9, color: "#888" }}>{r.user_email}</div>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div style={{ padding: "8px 16px", background: "#eceff1", fontSize: 11, color: "#37474f", textAlign: "right" }}>
              Totale eventi: <b>{logRows.length}</b>
            </div>
          </div>
        </div>
      )}

      {addebitiModal && (
        <div data-testid="mm-addebiti-modal" onClick={() => !addebitiBusy && setAddebitiModal(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1200, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "24px 16px", overflowY: "auto" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "white", width: "100%", maxWidth: 640, borderRadius: 10, overflow: "hidden", boxShadow: "0 10px 40px rgba(0,0,0,0.35)" }}>
            <div style={{ background: "#4a148c", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong style={{ fontSize: 15 }}>💳 Crea addebiti Segremat — {selCount} alunni</strong>
              <button onClick={() => !addebitiBusy && setAddebitiModal(false)} data-testid="mm-addebiti-close" style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 18 }}>✕</button>
            </div>
            <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
              {!addebitiResult ? (
                <>
                  <div style={{ fontSize: 12, color: "#546e7a", background: "#f3e5f5", padding: "8px 12px", borderRadius: 4 }}>
                    Verrà creato <b>un addebito per alunno</b> su Segremat (A.S. <b>{paramAnno}</b>), con la <b>stessa scadenza</b>. La descrizione è <b>proposta per settore</b>: Settore A senza numero pasti; Settori E/F/M con il numero pasti del singolo alunno (il simbolo <b>#</b> viene sostituito dalle presenze). L'importo è il valore mensa di ciascun alunno (colonna Valore €).
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700, color: "#455a64", display: "block", marginBottom: 4 }}>Data scadenza addebito *</label>
                    <input data-testid="mm-addebiti-scadenza" type="text" placeholder="gg/mm/aaaa" value={addebitiForm.data_scadenza}
                      onChange={(e) => setAddebitiForm(f => ({ ...f, data_scadenza: e.target.value }))}
                      style={{ width: "100%", padding: 9, border: "2px solid #7e57c2", borderRadius: 4, fontSize: 14, fontWeight: 600 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700, color: "#455a64", display: "block", marginBottom: 4 }}>Descrizione — Settore A (Infanzia) *</label>
                    <input data-testid="mm-addebiti-descrizione-a" type="text" maxLength={120} value={addebitiForm.descrizione_a}
                      onChange={(e) => setAddebitiForm(f => ({ ...f, descrizione_a: e.target.value }))}
                      style={{ width: "100%", padding: 9, border: "2px solid #7e57c2", borderRadius: 4, fontSize: 14 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700, color: "#455a64", display: "block", marginBottom: 4 }}>Descrizione — Settori E / F / M *</label>
                    <input data-testid="mm-addebiti-descrizione-efm" type="text" maxLength={120} value={addebitiForm.descrizione_efm}
                      onChange={(e) => setAddebitiForm(f => ({ ...f, descrizione_efm: e.target.value }))}
                      style={{ width: "100%", padding: 9, border: "2px solid #7e57c2", borderRadius: 4, fontSize: 14 }} />
                    <span style={{ fontSize: 11, color: "#607d8b" }}>Usa <b>#</b> dove va il numero pasti dell'alunno (es. "… pasti nr #").</span>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700, color: "#455a64", display: "block", marginBottom: 4 }}>Causale</label>
                    <input data-testid="mm-addebiti-causale" type="number" value={addebitiForm.causale}
                      onChange={(e) => setAddebitiForm(f => ({ ...f, causale: e.target.value }))}
                      style={{ width: 120, padding: 9, border: "2px solid #7e57c2", borderRadius: 4, fontSize: 14, fontWeight: 700 }} />
                    <span style={{ marginLeft: 10, fontSize: 12, color: "#607d8b" }}>Predefinito <b>2 = Mensa</b></span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 }}>
                    <button onClick={() => setAddebitiModal(false)} disabled={addebitiBusy}
                      style={{ padding: "9px 16px", border: "1px solid #b0bec5", background: "white", borderRadius: 4, fontWeight: 700, cursor: "pointer" }}>Annulla</button>
                    <button data-testid="mm-addebiti-invia" onClick={inviaAddebiti} disabled={addebitiBusy}
                      style={{ padding: "9px 22px", border: "none", background: addebitiBusy ? "#9575cd" : "#4a148c", color: "white", borderRadius: 4, fontWeight: 800, cursor: addebitiBusy ? "wait" : "pointer" }}>
                      {addebitiBusy ? "Creazione in corso…" : "INVIO"}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div data-testid="mm-addebiti-result" style={{ display: "flex", gap: 16, fontSize: 14, fontWeight: 800 }}>
                    <span style={{ color: "#1b5e20" }}>✓ Creati: {addebitiResult.n_ok}</span>
                    <span style={{ color: addebitiResult.n_err ? "#c62828" : "#90a4ae" }}>✗ Errori: {addebitiResult.n_err}</span>
                  </div>
                  <div style={{ maxHeight: "50vh", overflowY: "auto", border: "1px solid #eee", borderRadius: 4 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                      <thead><tr style={{ background: "#ede7f6" }}>
                        <th style={{ textAlign: "left", padding: "6px 8px" }}>Alunno</th>
                        <th style={{ textAlign: "right", padding: "6px 8px" }}>Importo</th>
                        <th style={{ textAlign: "left", padding: "6px 8px" }}>Esito</th>
                      </tr></thead>
                      <tbody>
                        {(addebitiResult.risultati || []).map((r, i) => (
                          <tr key={i} style={{ borderTop: "1px solid #f0f0f0", background: r.ok ? "#f1f8e9" : "#ffebee" }}>
                            <td style={{ padding: "5px 8px" }}>{r.cognome} {r.nome} <span style={{ color: "#999" }}>({r.codice_alunno})</span></td>
                            <td style={{ padding: "5px 8px", textAlign: "right", fontFamily: "monospace" }}>€ {Number(r.importo || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td style={{ padding: "5px 8px", color: r.ok ? "#1b5e20" : "#c62828" }}>{r.ok ? `✓ Mov. ${r.codice_movimento ?? ""}` : `✗ ${r.errore}`}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button data-testid="mm-addebiti-fine" onClick={() => { setAddebitiModal(false); fetchData(); }}
                      style={{ padding: "9px 22px", border: "none", background: "#4a148c", color: "white", borderRadius: 4, fontWeight: 800, cursor: "pointer" }}>Chiudi</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}


function MensaParamsModal({ annoScol, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/mensa-mese/params", { params: annoScol ? { anno_scol: annoScol } : {} });
        // Trasforma tutti i valori numerici in stringhe con 2 decimali per l'editing
        const raw = r.data || {};
        const formatted = {};
        for (const k of Object.keys(raw)) {
          if (typeof raw[k] === "number") formatted[k] = raw[k].toFixed(2);
          else formatted[k] = raw[k];
        }
        setData(formatted);
        // Avvisa se i valori provengono dall'AS precedente o dai defaults
        if (raw._source === "previous_as") {
          setMsg({ ok: true, text: `Valori copiati dall'AS ${annoScol - 1}/${String(annoScol).slice(2)}. Salva per confermarli.` });
        } else if (raw._source === "defaults") {
          setMsg({ ok: true, text: "Nessun valore salvato per questo AS: applicati i default. Salva per confermarli." });
        }
      } catch (e) {
        setMsg({ ok: false, text: "Errore caricamento: " + (e.response?.data?.detail || e.message) });
      }
    })();
  }, [annoScol]);

  const FIELDS = [
    { key: "costo_pasto_pp",        label: "COSTO PASTO PRIMARIA",   unit: "€", help: "Pasto Primaria (tariffa base)" },
    { key: "costo_pasto_sec",       label: "COSTO PASTO SECONDARIA", unit: "€", help: "Pasto Secondaria" },
    { key: "costo_pasto_agev",      label: "COSTO PASTO AGEVOLATI",  unit: "€", help: "Pasto Agevolato (flag Agevolato)" },
    { key: "sconto_50_assenze_pct", label: "SCONTO +50% ASSENZE MESE", unit: "%", help: "Sconto applicato se assenze > 50% del mese" },
    { key: "sconto_fratello_pct",   label: "SCONTO FRATELLO",        unit: "%", help: "Sconto applicato per fratelli (flag Fratelli)" },
    { key: "sconto_dicembre_pct",   label: "SCONTO DICEMBRE",        unit: "%", help: "Sconto applicato a Dicembre" },
    { key: "retta_base_infanzia",   label: "RETTA BASE INFANZIA",    unit: "€", help: "Retta base mensile Infanzia" },
    { key: "retta_no_residenti",    label: "RETTA NON RESIDENTI",    unit: "€", help: "Retta aggiuntiva non residenti (flag Non Residente)" },
    { key: "retta_min_infanzia",    label: "RETTA MINIMA INFANZIA",  unit: "€", help: "Retta minima Infanzia" },
    { key: "retta_privati",         label: "RETTA PRIVATI",          unit: "€", help: "Retta scuola privati (flag Privato)" },
    { key: "isee_minimo",            label: "VALORE ISEE MINIMO",     unit: "€", help: "Soglia ISEE minima per calcolo agevolazioni" },
    { key: "isee_massimo",           label: "VALORE ISEE MASSIMO",    unit: "€", help: "Soglia ISEE massima per calcolo agevolazioni" },
    { key: "pct_isee_minimo",        label: "PERCENTUALE ISEE MINIMO", unit: "%", help: "Percentuale applicata al valore ISEE minimo" },
    { key: "pct_isee_massimo",       label: "PERCENTUALE ISEE MASSIMO", unit: "%", help: "Percentuale applicata al valore ISEE massimo" },
  ];

  // Formattazione con 2 decimali (sostituisce il valore mostrato a blur)
  const fmt2 = (v) => {
    if (v === "" || v == null) return "";
    const n = Number(String(v).replace(",", "."));
    if (Number.isNaN(n)) return "";
    return n.toFixed(2);
  };

  const setField = (k, v) => {
    // Durante l'edit teniamo la stringa così com'è (l'utente potrebbe scrivere "8,5" o "8.")
    setData(prev => ({ ...prev, [k]: v }));
  };

  const onFieldBlur = (k) => {
    setData(prev => ({ ...prev, [k]: fmt2(prev[k]) }));
  };

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const payload = {};
      for (const f of FIELDS) {
        const n = Number(String(data[f.key] ?? "").replace(",", "."));
        if (!Number.isNaN(n) && data[f.key] !== "" && data[f.key] != null) {
          payload[f.key] = Number(n.toFixed(2));
        }
      }
      const r = await api.put("/mensa-mese/params", payload, { params: annoScol ? { anno_scol: annoScol } : {} });
      // Riformatta a 2 decimali dopo il save
      const raw = r.data || {};
      const formatted = {};
      for (const k of Object.keys(raw)) {
        if (typeof raw[k] === "number") formatted[k] = raw[k].toFixed(2);
        else formatted[k] = raw[k];
      }
      setData(formatted);
      setMsg({ ok: true, text: `Parametri salvati per AS ${annoScol}/${String(annoScol + 1).slice(2)}` });
    } catch (e) {
      setMsg({ ok: false, text: "Errore salvataggio: " + (e.response?.data?.detail || e.message) });
    } finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} data-testid="mensa-mese-params-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "white", width: "min(640px, 95vw)", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 30px rgba(0,0,0,0.4)", border: "2px solid #6a1b9a" }}>
        <div style={{ background: "linear-gradient(180deg, #6a1b9a 0%, #4a148c 100%)", color: "white", padding: "12px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.85, letterSpacing: 0.5, textTransform: "uppercase" }}>Mensa Mese · AS {annoScol}/{String(annoScol + 1).slice(2)}</div>
            <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: 0.3 }}>⚙ Parametri della Gestione</div>
          </div>
          <button onClick={onClose} data-testid="mensa-mese-params-close"
            style={{ background: "transparent", color: "white", border: "1px solid rgba(255,255,255,0.5)", padding: "4px 14px", cursor: "pointer", fontSize: 14, fontWeight: 700 }}>Chiudi ✕</button>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 18 }}>
          {!data ? (
            <div style={{ padding: 30, textAlign: "center", color: "#666" }}>Caricamento...</div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "10px 14px", alignItems: "center" }}>
              {FIELDS.map((f, idx) => {
                // Inserisci un separatore prima della prima percentuale e della prima retta
                const prevUnit = idx > 0 ? FIELDS[idx - 1].unit : null;
                const showSep = idx > 0 && prevUnit !== f.unit;
                return (
                  <React.Fragment key={f.key}>
                    {showSep && <div style={{ gridColumn: "1 / span 2", height: 1, background: "#e0e0e0", margin: "6px 0" }} />}
                    <div title={f.help} style={{ fontSize: 13, fontWeight: 700, color: "#4a148c", textTransform: "uppercase", letterSpacing: 0.3 }}>
                      {f.label}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#f3e5f5", padding: "6px 10px", border: "1px solid #ce93d8", minWidth: 160 }}>
                      <span style={{ fontWeight: 800, color: "#4a148c", fontSize: 14, minWidth: 14 }}>{f.unit}</span>
                      <input type="text" inputMode="decimal"
                        value={data[f.key] ?? ""}
                        onChange={e => setField(f.key, e.target.value)}
                        onBlur={() => onFieldBlur(f.key)}
                        data-testid={`mensa-params-${f.key}`}
                        style={{ flex: 1, padding: "4px 6px", fontSize: 14, fontWeight: 800, fontFamily: "monospace", border: "1px solid #ba68c8", color: "#4a148c", textAlign: "right" }} />
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          )}
          {msg && (
            <div style={{ marginTop: 14, padding: "8px 12px", background: msg.ok ? "#e8f5e9" : "#ffebee", border: `1px solid ${msg.ok ? "#66bb6a" : "#ef5350"}`, color: msg.ok ? "#1b5e20" : "#c62828", fontSize: 12, fontWeight: 700 }}>
              {msg.ok ? "✅" : "⚠️"} {msg.text}
            </div>
          )}
        </div>
        <div style={{ padding: "10px 18px", borderTop: "1px solid #e0e0e0", display: "flex", justifyContent: "flex-end", gap: 8, background: "#fafafa" }}>
          <button onClick={onClose} style={{ padding: "8px 18px", background: "white", color: "#666", border: "1px solid #999", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>Annulla</button>
          <button onClick={save} disabled={busy || !data} data-testid="mensa-mese-params-save"
            style={{ padding: "8px 22px", background: busy ? "#90a4ae" : "linear-gradient(180deg, #6a1b9a 0%, #4a148c 100%)", color: "white", border: "none", fontWeight: 800, fontSize: 13, cursor: busy ? "wait" : "pointer", letterSpacing: 0.5 }}>
            {busy ? "Salvataggio..." : "💾 Salva parametri"}
          </button>
        </div>
      </div>
    </div>
  );
}


function IseeStoricoModal({ data, onClose, onChanged }) {
  const { alunno, list } = data;
  const [form, setForm] = useState({ anno: new Date().getFullYear(), importo: "", data_emissione: "", data_scadenza: "", data_ricezione: "" });
  const [busy, setBusy] = useState(false);
  const formatData = (s) => {
    if (!s) return "";
    const t = String(s).slice(0, 10);
    if (t.includes("-")) {
      const [y, m, d] = t.split("-");
      return `${d}/${m}/${y}`;
    }
    return t;
  };
  const submit = async () => {
    if (!form.anno || !form.importo) { alert("Anno e Importo sono obbligatori"); return; }
    setBusy(true);
    try {
      await api.post("/mensa-mese/isee", {
        cod_fis: alunno.CodFis,
        anno: parseInt(form.anno, 10),
        importo: parseFloat(form.importo),
        data_emissione: form.data_emissione || null,
        data_scadenza: form.data_scadenza || null,
        data_ricezione: form.data_ricezione || null,
      });
      setForm({ anno: new Date().getFullYear(), importo: "", data_emissione: "", data_scadenza: "", data_ricezione: "" });
      onChanged();
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };
  const delRecord = async (id) => {
    if (!window.confirm(`Eliminare il record ISEE id=${id}?`)) return;
    try {
      await api.delete(`/mensa-mese/isee/by-id/${id}`);
      onChanged();
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    }
  };
  return (
    <div onClick={onClose} data-testid="mm-isee-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "white", width: "min(900px, 95vw)", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 30px rgba(0,0,0,0.4)", border: "2px solid #00838f" }}>
        <div style={{ background: "#00838f", color: "white", padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.85, letterSpacing: 0.5, textTransform: "uppercase" }}>Storico ISEE</div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>{alunno.Cog} {alunno.Nom}</div>
            <div style={{ fontSize: 11, opacity: 0.9, fontFamily: "monospace" }}>{alunno.CodFis}</div>
          </div>
          <button onClick={onClose} data-testid="mm-isee-modal-close"
            style={{ background: "transparent", color: "white", border: "1px solid rgba(255,255,255,0.5)", padding: "4px 14px", cursor: "pointer", fontSize: 14, fontWeight: 700 }}>Chiudi ✕</button>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6, color: "#00838f" }}>📜 Storico ISEE registrati ({list.length})</div>
          {list.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", color: "#888", border: "1px dashed #ccc" }}>Nessun record ISEE per questo alunno.</div>
          ) : (
            <table className="sw-table" style={{ fontSize: 12 }}>
              <thead><tr>
                <th style={{ width: 60, textAlign: "center" }}>ID</th>
                <th style={{ width: 70 }}>Anno</th>
                <th style={{ width: 120, textAlign: "right" }}>Importo</th>
                <th>Emissione</th>
                <th>Scadenza</th>
                <th>Ricezione</th>
                <th style={{ width: 60 }}></th>
              </tr></thead>
              <tbody>
                {list.map(r => (
                  <tr key={r.id} data-testid={`mm-isee-row-${r.id}`}>
                    <td style={{ textAlign: "center", fontFamily: "monospace", fontWeight: 700, color: "#00838f" }}>#{r.id}</td>
                    <td style={{ fontWeight: 700 }}>{r.anno}</td>
                    <td style={{ textAlign: "right", fontFamily: "monospace", fontWeight: 700, color: "#1b5e20" }}>€ {Number(r.importo).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td>{formatData(r.data_emissione)}</td>
                    <td>{formatData(r.data_scadenza)}</td>
                    <td>{formatData(r.data_ricezione)}</td>
                    <td style={{ textAlign: "center" }}>
                      <button onClick={() => delRecord(r.id)} title="Elimina" style={{ padding: "1px 6px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 10, cursor: "pointer" }}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ marginTop: 18, padding: 12, background: "#e0f7fa", border: "1px solid #00838f" }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: "#006064" }}>➕ Aggiungi nuovo record ISEE</div>
            <div style={{ display: "grid", gridTemplateColumns: "100px 140px 140px 140px 140px", gap: 8 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: "#006064", letterSpacing: 0.3 }}>ANNO *</label>
                <input type="number" placeholder="Anno *" value={form.anno} onChange={e => setForm({ ...form, anno: e.target.value })} data-testid="mm-isee-form-anno" style={{ padding: 6, fontSize: 12, border: "1px solid #ccc" }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: "#006064", letterSpacing: 0.3 }}>IMPORTO € *</label>
                <input type="number" step="0.01" placeholder="Importo € *" value={form.importo} onChange={e => setForm({ ...form, importo: e.target.value })} data-testid="mm-isee-form-importo" style={{ padding: 6, fontSize: 12, border: "1px solid #ccc" }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: "#006064", letterSpacing: 0.3 }}>EMISSIONE</label>
                <input type="date" title="Data di emissione dell'attestazione ISEE" value={form.data_emissione} onChange={e => setForm({ ...form, data_emissione: e.target.value })} style={{ padding: 6, fontSize: 12, border: "1px solid #ccc" }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: "#006064", letterSpacing: 0.3 }}>SCADENZA</label>
                <input type="date" title="Data di scadenza dell'attestazione ISEE" value={form.data_scadenza} onChange={e => setForm({ ...form, data_scadenza: e.target.value })} style={{ padding: 6, fontSize: 12, border: "1px solid #ccc" }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: "#006064", letterSpacing: 0.3 }}>RICEZIONE</label>
                <input type="date" title="Data di ricezione del documento ISEE da parte della scuola" value={form.data_ricezione} onChange={e => setForm({ ...form, data_ricezione: e.target.value })} style={{ padding: 6, fontSize: 12, border: "1px solid #ccc" }} />
              </div>
            </div>
            <button onClick={submit} disabled={busy} data-testid="mm-isee-form-submit"
              style={{ marginTop: 10, padding: "8px 16px", background: "#00838f", color: "white", border: "none", fontWeight: 800, fontSize: 12, cursor: busy ? "wait" : "pointer", letterSpacing: 0.5 }}>
              {busy ? "Salvataggio..." : "+ Salva record"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function GiorniPresenzaModal({ alunno, mese, anno, onClose }) {
  const giorni = Array.isArray(alunno.giorni) ? alunno.giorni : [];
  const giorniSettimana = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
  const parsed = giorni
    .map(s => {
      const m = String(s).match(/^(\d{2})-(\d{2})-(\d{4})$/);
      if (!m) return { raw: s, dt: null, weekday: "—" };
      const dt = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
      return { raw: s, dt, weekday: giorniSettimana[dt.getDay()], display: `${m[1]}/${m[2]}/${m[3]}` };
    })
    .sort((a, b) => (a.dt && b.dt) ? a.dt - b.dt : 0);

  const MESI_NOMI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

  return (
    <div onClick={onClose} data-testid="mensa-mese-giorni-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "white", width: "min(560px, 96vw)", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 10px 30px rgba(0,0,0,0.35)", border: "2px solid #6a1b9a" }}>
        <div style={{ background: "linear-gradient(180deg,#6a1b9a 0%,#4a148c 100%)", color: "white", padding: "12px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.85, letterSpacing: 0.4, textTransform: "uppercase" }}>Giornate di presenza · {MESI_NOMI[mese - 1]} {anno}</div>
            <div style={{ fontSize: 16, fontWeight: 800, marginTop: 2 }}>{alunno.Cog} {alunno.Nom}</div>
            <div style={{ fontSize: 11, opacity: 0.85, fontFamily: "monospace", marginTop: 2 }}>CF: {alunno.CodFis || "—"} · Classe: {alunno.classe || "—"}</div>
          </div>
          <button onClick={onClose} data-testid="mensa-mese-giorni-close"
            style={{ background: "transparent", color: "white", border: "1px solid rgba(255,255,255,0.5)", padding: "4px 14px", cursor: "pointer", fontSize: 14, fontWeight: 700 }}>✕</button>
        </div>
        <div style={{ padding: "10px 18px", borderBottom: "1px solid #e0e0e0", background: "#f3e5f5", fontSize: 13, fontWeight: 700, color: "#4a148c", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Totale presenze: <span style={{ fontSize: 18, color: "#4a148c", fontFamily: "monospace" }}>{alunno.presenze || 0}</span></span>
          <span style={{ fontSize: 11, opacity: 0.85 }}>Date dal file Excel importato</span>
        </div>
        <div style={{ overflow: "auto", maxHeight: "60vh" }}>
          {parsed.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: "#999", fontStyle: "italic" }}>
              Nessuna giornata registrata.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#ede7f6", color: "#4a148c" }}>
                  <th style={{ padding: "8px 12px", textAlign: "left", width: 50, borderBottom: "1px solid #ce93d8" }}>#</th>
                  <th style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #ce93d8" }}>Data</th>
                  <th style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #ce93d8" }}>Giorno</th>
                </tr>
              </thead>
              <tbody>
                {parsed.map((g, i) => (
                  <tr key={g.raw} data-testid={`mensa-mese-giorno-${i}`}
                    style={{ background: i % 2 ? "#fafafa" : "white" }}>
                    <td style={{ padding: "6px 12px", color: "#999", fontFamily: "monospace" }}>{i + 1}</td>
                    <td style={{ padding: "6px 12px", fontFamily: "monospace", fontWeight: 700, color: "#4a148c" }}>{g.display || g.raw}</td>
                    <td style={{ padding: "6px 12px", color: g.weekday === "Sab" || g.weekday === "Dom" ? "#c62828" : "#37474f", fontWeight: g.weekday === "Sab" || g.weekday === "Dom" ? 700 : 400 }}>{g.weekday}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
